import PublicNavigation from './PublicNavigation';
import { isFlowTestEmail } from './flowTesting';
import { savedIntake, saveIntake, restoreIntakeFile, keepIntakeFile, clearIntake } from './intakeStorage';
import VoiceInput from './VoiceInput';
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import "./autofundraiser.css";
import AutoFundraiserLogo from "./AutoFundraiserLogo";
import useMeetingRecording from "./useMeetingRecording";
import FundraisingStrategyOffer from "./FundraisingStrategyOffer";
import StrategySubmissionComplete from "./StrategySubmissionComplete";
import StrategyNextSteps from "./StrategyNextSteps";
import { readableBrandColor } from "./brandingColors";
import { firstName, strategyProgress, deliveryMessage } from "./journeyCopy";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/autofundraiser`;
const AUTH_TOKEN = "autoFundraiserSession";
export const authHeaders = () => {
  const testResume = sessionStorage.getItem("autoFundraiserTestResume");
  const testSession = testResume && (window.location.pathname.includes(`/${testResume}`) || new URLSearchParams(window.location.search).get("resume_token") === testResume) ? sessionStorage.getItem("autoFundraiserTestSession") : "";
  if (testSession) return { Authorization: `Bearer ${testSession}` };
  const session = localStorage.getItem(AUTH_TOKEN);
  return session ? { Authorization: `Bearer ${session}` } : {};
};

function CopyLinkButton({ value, label = "COPY LINK" }) {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  return <div className="af-copy-control"><button className="af-secondary" onClick={async () => {
    setError("");
    try { await navigator.clipboard.writeText(value); setCopied(true); }
    catch { setError("Select and copy the link above; this browser blocked clipboard access."); }
  }}>{copied ? "LINK COPIED" : label}</button>{error && <small role="status">{error}</small>}</div>;
}

export const STRATEGY_QUESTIONS = [
  {
    key: "1",
    audience: "Individuals",
    step: "WHO",
    title: "Which types of people have the strongest reasons to give to your mission, and why would they care enough to give?",
    hint: "Not everybody will fund your mission. Tell us the types of people you believe have the strongest reasons to support this work and why. Think about who benefits when your mission succeeds, who cares deeply about the problem you solve, who has lived through it, who is connected to the people you serve, and who can give, is willing to give, and cares about your mission. Give us types of people, not individual names.",
  },
  {
    key: "2",
    audience: "Individuals",
    step: "WHERE",
    title: "Where do these people already gather, belong or pay attention?",
    hint: "Once you know exactly who you are looking for, fundraising becomes less like searching everywhere. Tell us the places, communities, associations, networks, platforms or other spaces where these people can consistently be found.",
  },
  {
    key: "3",
    audience: "Individuals",
    step: "ATTRACT",
    title: "What can you put in front of these people that would make them willingly come closer to your organization?",
    hint: "You do not always have to chase the next donor. A short guide, a useful report, a survey or a chance to share their ideas could give them a reason to come closer. Think about something useful or meaningful to this audience that can attract their attention, get them to identify themselves or share their contact details, and open the relationship before you ask for money.",
  },
  {
    key: "4",
    audience: "Individuals",
    step: "RAISE",
    title: "Once these people come into your world, what should happen before and after you ask them for money?",
    hint: "Build the relationship journey. How will they get to Know you, Like what you do and Trust your ability to create impact and manage resources? Then how will you Ask, Follow up respectfully if they do not give immediately, and Steward them so the relationship can continue after the gift?",
  },
  {
    key: "5",
    audience: "Individuals",
    step: "ASK",
    title: "What exactly will you ask these people to support, how much will you ask for, and why will the ask matter to them?",
    hint: "Your fundraising goal is not yet the ask. Tell us the amount, what their money will do, and why this audience would care. This is the Double Impact Fundraising Ask: their gift can do good for the mission and give them something meaningful in return, such as useful resources or the joy of helping people they care about. Only include a benefit you can actually provide.",
  },
  {
    key: "6",
    audience: "Businesses",
    step: "WHO",
    title: "Which types of businesses have the strongest reasons to support your mission, and why would supporting you make sense to them?",
    hint: "Tell us the types of businesses you believe have the strongest reasons to support this work and why. Think about businesses that serve the people you serve, benefit when your community does well, share your values, depend on talent or resources from your community, or have another strong connection to the problem you solve. Give us types of businesses, not company names.",
  },
  {
    key: "7",
    audience: "Businesses",
    step: "WHERE",
    title: "Where do these businesses and the people who make funding decisions already gather?",
    hint: "Tell us where the right executives, owners or decision-makers can consistently be found, including the associations, business networks, sector groups, events, directories, platforms or communities they already use.",
  },
  {
    key: "8",
    audience: "Businesses",
    step: "ATTRACT",
    title: "What can you put in front of these businesses that gives them a reason to open a conversation with you?",
    hint: "Instead of beginning with a sponsorship request, think about useful insight, information, access or value that matters to this type of business and can make the right decision-makers want to know more about your work.",
  },
  {
    key: "9",
    audience: "Businesses",
    step: "RAISE",
    title: "Once a business knows about you, what journey should move that relationship toward funding?",
    hint: "Show us how the business will get to Know your organization, Like the impact you create and Trust your ability to deliver and manage resources. Then explain how you will Ask, Follow up and Steward the relationship after support is given.",
  },
  {
    key: "10",
    audience: "Businesses",
    step: "ASK",
    title: "What exactly will you ask these businesses to support, how much will you ask for, and what do they receive alongside the impact they create?",
    hint: "Build the ask around both sides of the decision. Be clear about the amount and mission impact, then describe any real value, visibility, access, recognition or other benefit that makes the partnership meaningful to the business.",
  },
  {
    key: "11",
    audience: "Grantors",
    step: "WHO",
    title: "Which types of grantors have the strongest reasons to fund your mission, and why does your work fit what they want to fund?",
    hint: "Tell us the types of foundations, agencies or other grantmakers you believe are the strongest fit and why. Which ones fund the problem you solve, the people you serve or the place where you work? Tell us why those funding interests fit your mission. Give us types of grantors, not just the name of one funder.",
  },
  {
    key: "12",
    audience: "Grantors",
    step: "WHERE",
    title: "Where will you consistently find the grantors whose priorities fit your work?",
    hint: "Tell us where you will find and understand the right opportunities, including funder networks, databases, local or national foundations, government opportunities, program officers, sector groups or other places where relevant grantmakers can be identified.",
  },
  {
    key: "13",
    audience: "Grantors",
    step: "ATTRACT",
    title: "What can you share that makes the right grantors pay attention to your organization before an application is due?",
    hint: "Think about credible insight, research, results, community knowledge or other value that demonstrates your understanding of the need and your ability to create impact. The goal is to build credibility and open the relationship before the application becomes the only conversation.",
  },
  {
    key: "14",
    audience: "Grantors",
    step: "RAISE",
    title: "What relationship journey should take the right grantor from awareness to funding and continued support?",
    hint: "Show us how you will help them Know your work, Like the impact you create and Trust your ability to deliver. Then explain how you will make the Ask through the right application or conversation, Follow up properly and Steward the relationship after funding.",
  },
  {
    key: "15",
    audience: "Grantors",
    step: "ASK",
    title: "What exactly will you ask a grantor to fund, how much will you ask for, and what full impact will that funding create?",
    hint: "Be clear about the amount and the primary result the grantor cares about. Then show any connected impact the same funding can create, so the request communicates the full value of what their investment makes possible.",
  },
];




const errorMessage = (error, fallback) => {
  const detail = error.response?.data?.detail;
  if (typeof detail === "string" && !/<html|cloudflare|origin web server/i.test(detail)) return detail;
  return error.generationMessage || fallback;
};

export const postGeneration = async (url, body, config) => {
  const response = await axios.post(url, body, config);
  const job = response.data.generation_job;
  if (!job) return response;
  const deadline = Date.now() + 10 * 60 * 1000;
  while (Date.now() < deadline) {
    let data;
    try {
      ({ data } = await axios.get(`${API}/generation-jobs/${job.job_id}`, config));
    } catch (error) {
      if (error.response && ![502, 503, 504, 520, 522, 524].includes(error.response.status)) throw error;
      await new Promise((resolve) => setTimeout(resolve, 2000));
      continue;
    }
    if (data.status === "completed") return { ...response, data: data.result, status: 200 };
    if (data.status === "failed") {
      const error = new Error(data.error);
      error.generationMessage = data.error;
      throw error;
    }
    await new Promise((resolve) => setTimeout(resolve, 1000));
  }
  const error = new Error("Your generation request is saved. Reopen this page or retry to check its progress.");
  error.generationMessage = error.message;
  throw error;
};

function Logo({ compact = false }) {
  return <a href="/" className={compact ? "af-logo af-logo-compact" : "af-logo"} aria-label="Auto Fundraiser home">
    <AutoFundraiserLogo compact={compact} />
  </a>;
}

export function Shell({ children, narrow = false, footerText = "© 2026 Auto Fundraiser" }) {
  const params = useParams();
  const workspace = params.token && /^\/(strategy|meeting|workspace|execution)\//.test(window.location.pathname) && !window.location.pathname.startsWith("/execution/preferences/");
  const testResume = sessionStorage.getItem("autoFundraiserTestResume");
  const isTest = testResume && window.location.pathname.includes(`/${testResume}`);
  return <div className="af-app">
    <header className="af-header af-public-header"><Logo compact /><PublicNavigation />{workspace && <a className="af-workspace-nav" href={`/workspace/${params.token}`}>MY WORKSPACE</a>}</header>
    <main className={narrow ? "af-shell af-shell-narrow" : "af-shell"}>{isTest && <div className="af-admin-test-note"><strong>ADMIN TEST JOURNEY</strong><p>Payments and emails are skipped. Submitting your strategy uses your configured AI credits.</p><a href="/admin">Return to admin dashboard</a></div>}{children}</main>
    <footer className="af-footer">{footerText}</footer>
  </div>;
}

function Spinner({ label = "We are reviewing your fundraising approach…" }) {
  return <div className="af-thinking">
    <div className="af-orbit"><span /><span /><span /></div>
    <h2>{label}</h2>
    <p>We are looking closely at the strategy underneath your fundraising activities.</p>
  </div>;
}

export function AutoFundraiserHomePage() {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [saved] = useState(savedIntake);
  const [approach, setApproach] = useState(saved.approach || '');
  const [file, setFile] = useState(null);
  const [sourceMode, setSourceMode] = useState(saved.sourceMode || '');
  const noStrategy = sourceMode === 'none';
  const [capture, setCapture] = useState(!!saved.capture);
  const [identity, setIdentity] = useState({ name: '', email: '', organization_name: '', mission: '', people_served: '', location: '', website: '', fundraising_goal: '', target_period: '', ...saved.identity });
  const submissionId = useRef(saved.submissionId || crypto.randomUUID());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  useEffect(() => { saveIntake({ approach, sourceMode, capture, identity, submissionId: submissionId.current }); }, [approach, sourceMode, capture, identity]);
  useEffect(() => { let active = true; restoreIntakeFile().then((value) => { if (active) setFile(value); }).catch(() => {}); return () => { active = false; }; }, []);
  const changeFile = (value) => { if (value?.size > 8 * 1024 * 1024) { setError('Please upload a file smaller than 8MB.'); return; } setFile(value); keepIntakeFile(value).catch(() => {}); };

  useEffect(() => {
    const load = () => axios.get(`${API}/homepage`).then(({ data }) => setContent(data)).catch(() => setContent((current) => current || {}));
    load(); window.addEventListener('focus', load);
    return () => window.removeEventListener('focus', load);
  }, []);

  const text = content?.text || {};
  const testimonials = content?.testimonials || [];
  const shownTestimonial = testimonials[testimonialIndex % Math.max(1, testimonials.length)];
  useEffect(() => { if (text.page_title) document.title = text.page_title; }, [text.page_title]);

  const canContinue = noStrategy || (sourceMode === 'describe' && Boolean(approach.trim())) || (sourceMode === 'upload' && Boolean(file));

  const openCapture = () => {
    if (!canContinue) return;
    setCapture(true);

  };

  const submit = async () => {
    if (!identity.name.trim() || !identity.email.trim() || !identity.organization_name.trim()) return;
    setBusy(true); setError("");
    const form = new FormData();
    form.append("name", identity.name.trim());
    form.append("email", identity.email.trim());
    form.append("organization_name", identity.organization_name.trim());
    form.append("approach", sourceMode === 'describe' ? approach.trim() : '');
    form.append("no_strategy", noStrategy ? "true" : "false");
    form.append('clarification_required', 'true');
    form.append('strategy_experience', 'unified');
    form.append('entry_source', sessionStorage.getItem('autoFundraiserEntrySource') || 'autofundraiser');
    form.append('flow_submission_id', submissionId.current);
    for (const key of ['mission', 'people_served', 'location', 'website', 'target_period', 'fundraising_goal']) if (String(identity[key] ?? '').trim()) form.append(key, identity[key]);
    const savedReview = !isFlowTestEmail(identity.email) && localStorage.getItem(`afReview:${identity.email.trim().toLowerCase()}`);
    if (savedReview) form.append("resume_token", savedReview);
    if (sourceMode === 'upload' && file) form.append("file", file);
    try {
      const { data } = await axios.post(`${API}/leads`, form);
      if (!isFlowTestEmail(identity.email)) localStorage.setItem(`afReview:${identity.email.trim().toLowerCase()}`, data.resume_token);
      clearIntake();
      navigate(data.clarification_pending ? `/clarify/${data.resume_token}` : `/strategy-development/assessment/${data.resume_token}`);
    } catch (err) {
      setError(errorMessage(err, text.review_error));
      setBusy(false);
    }
  };

  if (!content) return <div className="af-home"><header className="af-home-top af-public-header"><Logo /><PublicNavigation /></header></div>;

  return <div className="af-home">
    <header className="af-home-top af-public-header"><Logo /><PublicNavigation /></header>
    <main className="af-hero">
      <h1>{content.problem_statement}</h1>
      <h2 className="af-support">{content.supporting_text}</h2>
      <div className="af-home-introduction"><p><strong>{content.introduction_text}</strong></p></div>

      <div className="af-composer">
        <div className="af-composer-intro">
          <h2>{content.form_heading}</h2>
        </div>
        <div className="af-source-choices" aria-label="Choose how to share your fundraising approach">
          <button type="button" className={sourceMode === 'describe' ? 'af-primary' : 'af-secondary'} aria-expanded={sourceMode === 'describe'} aria-controls="af-describe-approach" onClick={() => setSourceMode('describe')}>{text.describe_choice}</button>
          <button type="button" className={sourceMode === 'upload' ? 'af-primary' : 'af-secondary'} aria-expanded={sourceMode === 'upload'} aria-controls="af-upload-plan" onClick={() => setSourceMode('upload')}>{text.upload_choice}</button>
        </div>
        {sourceMode === 'describe' && <div id="af-describe-approach"><VoiceInput label={text.approach_label} buttonLabel={text.voice_button} stopLabel={text.voice_stop} startError={text.voice_error_start} stoppedError={text.voice_error_stopped} hideLabel rows={5} value={approach} onChange={setApproach} placeholder={content.composer_placeholder} /></div>}
        {sourceMode === 'upload' && <div id="af-upload-plan"><div className="af-composer-tools">
          <label className="af-upload">
            <input type="file" aria-label="Your fundraising plan" accept=".pdf,.docx,.txt,.md,.csv" hidden
              onChange={(event) => changeFile(event.target.files?.[0] || null)} />
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 17V5m0 0L7 10m5-5 5 5M5 19h14" /></svg>
            {file ? text.upload_change : text.upload_choose}
          </label>
          {file && <button type="button" className="af-file-clear" onClick={() => changeFile(null)}>{text.upload_remove}</button>}
        </div>
        {file && <div className="af-upload-status" role="status">
          <strong>{text.upload_attached}</strong>
          <span>{file.name}</span>
          <small>{text.upload_ready}</small>
        </div>}</div>}
        <div className="af-source-none"><label className={`af-none ${noStrategy ? "active" : ""}`}>
          <input type="checkbox" checked={noStrategy} onChange={(event) => setSourceMode(event.target.checked ? 'none' : '')} />
          {content.no_strategy_label || "I don't have a fundraising strategy"}
        </label></div>
      </div>

      {!capture ? <button className="af-primary af-primary-wide" disabled={!canContinue} onClick={openCapture}>
        {content.cta || "REVIEW MY FUNDRAISING FOR FREE"}
        <span>→</span>
      </button> : <section className="af-identity-card">
        <div>
          <span className="af-step-chip">{text.identity_kicker}</span>
          <h2>{text.identity_title}</h2>
          <p>{text.identity_description}</p>
        </div>
        <div className="af-fields-three">
          <label>{text.identity_name}<input id="af-name" autoFocus value={identity.name} onChange={(e) => setIdentity((current) => ({ ...current, name: e.target.value }))} /></label>
          <label>{text.identity_email}<input type="email" value={identity.email} onChange={(e) => setIdentity((current) => ({ ...current, email: e.target.value }))} /></label>
          <label>{text.identity_organization}<input value={identity.organization_name} onChange={(e) => setIdentity((current) => ({ ...current, organization_name: e.target.value }))} /></label>
        </div>
        <div className="af-form-stack">
          {[['mission', 'identity_mission'], ['people_served', 'identity_people'], ['location', 'identity_location']].map(([key, label]) => <label key={key}>{text[label]}<textarea aria-label={text[label]} value={identity[key] || ''} onChange={(e) => setIdentity({ ...identity, [key]: e.target.value })} /></label>)}
          <label>{text.identity_website}<input type="url" value={identity.website || ''} onChange={(e) => setIdentity({ ...identity, website: e.target.value })} /></label>
          <label>{text.identity_goal}<input type="number" min="1" value={identity.fundraising_goal || ''} onChange={(e) => setIdentity({ ...identity, fundraising_goal: e.target.value })} /></label>
          <label>{text.identity_deadline}<input value={identity.target_period || ''} onChange={(e) => setIdentity({ ...identity, target_period: e.target.value })} /></label>
        </div>
        {error && <p className="af-error">{error}</p>}
        <button className="af-primary af-primary-wide" disabled={busy || !identity.name.trim() || !identity.email.trim() || !identity.organization_name.trim()} onClick={submit}>
          {busy ? text.identity_submitting : text.identity_submit} <span>→</span>
        </button>
      </section>}
      <p className="af-privacy">{content.free_review_caption}</p>
      {error && !capture && <p className="af-error">{error}</p>}
    </main>

    <section className="af-professional-intro af-work-card">
      {content.professional_photo && <img src={content.professional_photo} alt={text.professional_photo_alt} />}
      <h2>{text.professional_heading}</h2><p>{content.professional_intro}</p>
      <div className="af-game-controls"><button className="af-primary" onClick={() => { document.querySelector('.af-composer')?.scrollIntoView({ behavior: 'smooth' }); }}>{text.build_alone_button}</button><a className="af-secondary af-link-button" href="https://nonprofitboardbuilder.com">{text.build_board_button}</a></div>
    </section>
    {shownTestimonial && <section className="af-testimonial-strip" aria-label={text.testimonial_heading}>
      <div className="af-testimonial-heading">
        <span>{text.testimonial_heading}</span>
      </div>
      <div className="af-testimonial-carousel">
        <button className="af-testimonial-arrow" type="button" aria-label={text.testimonial_previous}
          onClick={() => setTestimonialIndex((current) => (current - 1 + testimonials.length) % testimonials.length)}>‹</button>
        <article className="af-testimonial-card">
          <p>“{shownTestimonial.quote}”</p>
          <div><strong>{shownTestimonial.name}</strong><span>{shownTestimonial.organization}</span></div>
        </article>
        <button className="af-testimonial-arrow" type="button" aria-label={text.testimonial_next}
          onClick={() => setTestimonialIndex((current) => (current + 1) % testimonials.length)}>›</button>
      </div>
      <div className="af-testimonial-count">{testimonialIndex + 1} / {testimonials.length}</div>
    </section>}
  </div>;
}

function FundraisingReviewReport({ diagnosis, onContinue }) {
  const boxes = [
    ["readback", "WHAT YOU TOLD US", diagnosis.what_you_told_us],
    ["gaps", "THE GAPS WE SEE", diagnosis.what_we_found],
    ["why", "WHY THESE GAPS NEED FIXING", diagnosis.why_it_matters],
  ];
  return <Shell narrow>
    <div className="af-kicker af-review-kicker">YOUR FREE FUNDRAISING REVIEW</div>
    <h1 className="af-review-title">I've reviewed what you shared.</h1>
    <div className="af-review-report">
      {boxes.map(([key, heading, paragraph]) => <section className="af-readback" key={key} data-report-box={key}>
        <h2>{heading}</h2><p>{paragraph}</p>
      </section>)}
    </div>
    <div className="af-report-action">
      <button className="af-primary af-primary-wide" onClick={onContinue}>
        LET'S BUILD IT TOGETHER
      </button>
    </div>
  </Shell>;
}

export const VERIFICATION_QUESTIONS = [
  ['WHO_INDIVIDUALS', 'Do you know the exact types of people with the strongest reasons to fund your mission?'],
  ['WHO_BUSINESSES', 'Do you know the exact types of businesses with the strongest reasons to fund your mission?'],
  ['WHO_GRANTORS', 'Do you know the types of grantors with the strongest reasons to fund your mission?'],
  ['WHERE', 'Do you know where to find the people, businesses and grantors with the strongest reasons to fund your mission?'],
  ['ATTRACT', 'Do you know how to attract the types of people, businesses and grantors meant to fund your mission?'],
  ['RAISE', 'Do you have a clear process for raising money from the people, businesses and grantors meant to fund your mission?'],
  ['ASK', 'Do you know exactly what to ask for when you get in front of the people, businesses and grantors with the strongest reasons to fund your mission?'],
  ['AMOUNT', 'Do you know exactly how much to ask the people, businesses and grantors meant to fund your mission when you get in front of them?'],
];
const CLARIFICATION_AUDIENCES = [['individuals', 'Individuals'], ['businesses', 'Businesses'], ['grantors', 'Grantors']];
const SCOPED_CLARIFICATIONS = ['WHERE', 'ATTRACT', 'RAISE', 'ASK', 'AMOUNT'];

export function AutoFundraiserClarificationPage() {
  const { token } = useParams(); const navigate = useNavigate();
  const [lead, setLead] = useState(null); const [answers, setAnswers] = useState({});
  const [selections, setSelections] = useState({});
  const [busy, setBusy] = useState(false); const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    document.title = 'Just for clarification | Auto Fundraiser';
    let active = true;
    let timer;
    const load = () => axios.get(`${API}/leads/${token}`).then(({ data }) => {
      if (!active) return;
      if (data.paid) { navigate((data.strategy_experience === "unified" || data.entry_source === "fundraising_game") ? `/strategy-development/workspace/${token}` : `/strategy/${token}`, { replace: true }); return; }
      setLead(data);
      setError('');
      setAnswers(data.verification_draft?.answers || Object.fromEntries(Object.entries(data.clarifications || {}).map(([key, item]) => [key, item.knows])));
      setSelections(data.verification_draft?.audience_selections || Object.fromEntries(SCOPED_CLARIFICATIONS.map((key) => [key, data.clarifications?.[key]?.knows === true ? data.clarifications[key].audiences || [] : []])));
    }).catch((err) => { if (active) {
      setError(errorMessage(err, 'We could not open your saved questions.'));
      if (!err.response || err.response.status >= 500) timer = setTimeout(load, 2000);
    } });
    load();
    return () => { active = false; clearTimeout(timer); };
  }, [token, navigate, attempt]);
  useEffect(() => {
    if (!lead || !dirty || busy || lead.verification_completed_at) return;
    const timer = setTimeout(() => {
      axios.post(`${API}/leads/${token}/verification`, { answers: Object.fromEntries(VERIFICATION_QUESTIONS.filter(([key]) => typeof answers[key] === 'boolean').map(([key]) => [key, answers[key]])), questionnaire_version: 2, audience_selections: selections, complete: false })
        .catch(() => {});
    }, 600);
    return () => clearTimeout(timer);
  }, [lead, dirty, busy, answers, selections, token]);
  const submit = async (event) => {
    event.preventDefault(); setBusy(true); setError('');
    try {
      await axios.post(`${API}/leads/${token}/verification`, { answers: Object.fromEntries(VERIFICATION_QUESTIONS.filter(([key]) => typeof answers[key] === 'boolean').map(([key]) => [key, answers[key]])), questionnaire_version: 2, audience_selections: selections });
      navigate(`/assessment-submitted/${token}`); window.scrollTo(0, 0);
    } catch (err) { setError(errorMessage(err, 'Your answers are still here. Please try saving them again.')); setBusy(false); }
  };
  if (!lead) return <Shell narrow>{error ? <><p role="alert" className="af-error">{error}</p><button className="af-primary" onClick={() => setAttempt(attempt + 1)}>OPEN MY SAVED QUESTIONS</button></> : <Spinner label="Opening your saved questions…" />}</Shell>;
  return <Shell narrow><div className="af-kicker">YOUR PLAN IS SAVED</div><h1>Let's look a little deeper into how your organization raises money.</h1>
    <p>You've shared your current approach. Before we assess its strengths and weaknesses, we'd like to understand a few important things about how your fundraising works.</p>
    <p>Your answers will help us identify whether your current strategy gives your organization a clear path towards the funding it needs.</p>
    <form className="af-verification-form" onSubmit={submit}>
      {VERIFICATION_QUESTIONS.map(([area, defaultQuestion], index) => { const question = lead?.clarification_questionnaire?.[area] || defaultQuestion; return <fieldset key={area} data-verification-area={area}>
        <legend><span>{index + 1}.</span> {question}</legend>
        <p className="af-verification-question" aria-hidden="true">{index + 1}. {question}</p>
        <div className="af-verification-choices">{[[true, 'Yes'], [false, 'No']].map(([value, label]) => <label key={label} className={answers[area] === value ? 'selected' : ''}>
          <input type="radio" name={area} checked={answers[area] === value} disabled={busy} onChange={() => { setAnswers({ ...answers, [area]: value }); if (SCOPED_CLARIFICATIONS.includes(area) && !value) setSelections((current) => ({ ...current, [area]: [] })); setDirty(true); }} />{label}
        </label>)}</div>
        {SCOPED_CLARIFICATIONS.includes(area) && answers[area] === true && <div data-audience-selection={area}>
          <p>Select every audience this applies to.</p>
          <div className="af-verification-choices">{CLARIFICATION_AUDIENCES.map(([audience, label]) => <label key={audience} className={(selections[area] || []).includes(audience) ? 'selected' : ''}>
            <input type="checkbox" aria-label={label} checked={(selections[area] || []).includes(audience)} disabled={busy} onChange={(event) => { setSelections((current) => ({ ...current, [area]: event.target.checked ? [...(current[area] || []), audience] : (current[area] || []).filter((value) => value !== audience) })); setDirty(true); }} />{label}
          </label>)}</div>
        </div>}
      </fieldset>; })}
      {error && <p role="alert" className="af-error">{error}</p>}
      <button className="af-primary af-primary-wide" disabled={busy || VERIFICATION_QUESTIONS.some(([area]) => typeof answers[area] !== 'boolean') || SCOPED_CLARIFICATIONS.some((area) => answers[area] === true && !selections[area]?.length)}>{busy ? 'SAVING YOUR ANSWERS…' : 'SUBMIT MY ANSWERS FOR REVIEW'}</button>
    </form>
  </Shell>;
}

export function AutoFundraiserEmailPreferencesPage() {
  const { token } = useParams(); const [state, setState] = useState(null); const [error, setError] = useState('');
  useEffect(() => { axios.get(`${API}/follow-up/preferences/${token}`).then(({ data }) => setState(data))
    .catch((err) => setError(errorMessage(err, 'This preference link is not available.'))); }, [token]);
  return <Shell narrow><h1>{state?.opted_out ? 'Your email preference is saved.' : 'Your fundraising follow-up emails.'}</h1>
    {state?.opted_out ? <p>We have stopped these follow-up emails. Your saved review and strategy account are still available.</p> : state && <><p>You can stop the reminders about your free review and building your strategy.</p><button className="af-primary" onClick={async () => {
      try { const { data } = await axios.post(`${API}/follow-up/preferences/${token}`); setState(data); }
      catch (err) { setError(errorMessage(err, 'We could not save this preference. Please try again.')); }
    }}>STOP FOLLOW-UP EMAILS</button></>}
    {error && <p className="af-error" role="alert">{error}</p>}
  </Shell>;
}

export function AutoFundraiserReviewPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [retryStarted, setRetryStarted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const reportReady = Boolean(lead?.diagnosis && lead.review_status === "ready");

  useEffect(() => {
    document.title = "Your Fundraising Review | Auto Fundraiser";
    let cancelled = false;
    let timer;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/leads/${token}`);
        if (cancelled) return;
        if (data.clarification_pending) { navigate(`/clarify/${token}`, { replace: true }); return; }
        setLead(data);
        setError("");
        if (["queued", "running"].includes(data.review_status)) timer = setTimeout(load, 2000);
      } catch (err) {
        if (!cancelled) {
          setError(errorMessage(err, "Your plan is saved. We couldn't check its progress. Please try again."));
          if (!err.response || [502, 503, 504, 520, 522, 524].includes(err.response.status)) timer = setTimeout(load, 2000);
        }
      }
    };
    load();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [token, attempt, navigate]);

  useEffect(() => {
    if (reportReady) axios.post(`${API}/leads/${token}/result-viewed`).catch(() => {});
  }, [token, reportReady]);

  const retry = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/leads/${token}/retry-review`, {}, { timeout: 20000 });
      setLead(data);
      setRetryStarted(true);
      setAttempt((value) => value + 1);
    } catch (err) { setError(errorMessage(err, "Your plan is saved. We couldn't restart its review. Please try again.")); }
    finally { setBusy(false); }
  };

  if (error && !lead) return <Shell narrow><div className="af-message-card">
    <h1>Your review is saved.</h1><p>{error}</p>
    <button className="af-primary" onClick={() => setAttempt((value) => value + 1)}>CHECK MY SAVED REVIEW</button>
  </div></Shell>;
  if (!lead || ["queued", "running"].includes(lead.review_status)) return <Shell narrow>
    <Spinner label="We're reviewing your fundraising plan…" />
    <p>Your plan is saved. You can keep this page open or return to this same link while we finish the review.</p>
    {error && <p className="af-error">{error}</p>}
    {error && <button className="af-secondary" onClick={() => setAttempt((value) => value + 1)}>CHECK MY SAVED REVIEW</button>}
  </Shell>;
  if (lead.review_status === "failed") return <Shell narrow><div className="af-message-card">
    <h1>Your plan is saved.</h1>
    <p>{retryStarted ? "We retried your saved plan, but its review could not finish." : "We couldn't finish its review."}</p>
    <p>{lead.review_failure?.message || lead.analysis_error || "You can retry with the same email and the same uploaded plan."}</p>
    {lead.review_attempt > 0 && <p className="af-review-attempt">Review attempt {lead.review_attempt}. Your email and uploaded plan are still saved.</p>}
    <button className="af-primary" disabled={busy} onClick={retry}>{busy ? "RESTARTING…" : "RETRY MY SAVED REVIEW"}</button>
    {error && <p className="af-error">{error}</p>}
  </div></Shell>;
  if (!reportReady) return <Shell narrow><div className="af-message-card">
    <h1>Your plan is saved.</h1><p>We couldn't open its report. Please check your saved review again.</p>
    <button className="af-primary" onClick={() => setAttempt((value) => value + 1)}>CHECK MY SAVED REVIEW</button>
  </div></Shell>;

  return <FundraisingReviewReport diagnosis={lead.diagnosis} onContinue={() => {
    navigate(`/how-it-works/${token}`); window.scrollTo({ top: 0 });
  }} />;
}

export function AutoFundraiserResultPage() {
  return <AutoFundraiserReviewPage />;
}

export function AutoFundraiserHowItWorksPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [content, setContent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "How We Build Your Fundraising Strategy | Auto Fundraiser";
    Promise.all([axios.get(`${API}/leads/${token}`), axios.get(`${API}/homepage`).catch(() => ({ data: {} }))])
      .then(([leadResponse, homeResponse]) => {
        if (leadResponse.data.clarification_pending) { navigate(`/clarify/${token}`, { replace: true }); return; }
        if (!leadResponse.data.diagnosis) { navigate(`/review/${token}`, { replace: true }); return; }
        setLead(leadResponse.data); setContent(homeResponse.data);
      }).catch((err) => setError(errorMessage(err, "We could not open the strategy service. Your review is saved.")));
  }, [token, navigate]);

  const checkout = async (plan) => {
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/leads/${token}/checkout`, { plan });
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(errorMessage(err, "We could not start checkout."));
      setBusy(false);
    }
  };

  if (!lead || !content) return <Shell narrow>{error ? <p className="af-error">{error}</p> : <Spinner label="Preparing how we build your fundraising strategy…" />}</Shell>;

  return <Shell>
    <a className="af-text-button" href={`/result/${token}`}>Back to my fundraising review</a>
    <FundraisingStrategyOffer workflow={content.workflow} noStrategy={lead.no_strategy} diagnosis={lead.diagnosis} organization={lead.organization_name} hasGaps={Boolean(lead.diagnosis.gaps?.length)} busy={busy} checkout={checkout} priceCents={content.price_cents ?? 49700} />
    {error && <p className="af-error">{error}</p>}
  </Shell>;
}

export function AutoFundraiserPaymentSuccessPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    document.title = "Confirming Payment | Auto Fundraiser";
    const sessionId = new URLSearchParams(location.search).get("session_id");
    if (!sessionId) { setError("The payment link is missing its session. Please return using the link from checkout."); return; }
    let cancelled = false;
    let timer;
    let checks = 0;
    setError("");
    const verify = async () => {
      try {
        const { data } = await axios.get(`${API}/checkout-status?session_id=${encodeURIComponent(sessionId)}`);
        if (cancelled) return;
        if (!data.paid) {
          if (++checks < 30) { timer = setTimeout(verify, 2000); return; }
          setError("We are still waiting for payment confirmation. You can check again below.");
          return;
        }
        const resumeToken = encodeURIComponent(data.resume_token || "");
        if (!resumeToken) throw new Error("Your payment has been confirmed, but we could not open its saved review. Please contact support.");
        if (data.account_exists) {
          navigate(`/login?resume_token=${resumeToken}&email=${encodeURIComponent(data.email || "")}`, { replace: true });
        } else {
          navigate(`/account/setup?token=${resumeToken}`, { replace: true });
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || err.message || "We could not verify your payment yet. Please try again.");
      }
    };
    verify();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [location.search, attempt, navigate]);
  return <Shell narrow>{error ? <div className="af-message-card"><h1>Let's finish confirming your payment.</h1><p>{error}</p><button className="af-primary af-primary-wide" onClick={() => setAttempt((value) => value + 1)}>CHECK PAYMENT AGAIN</button></div> : <Spinner label="Checking your payment and opening your fundraising workspace…" />}</Shell>;
}

export function AutoFundraiserStrategyPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [question, setQuestion] = useState(1);
  const [answer, setAnswer] = useState("");
  const [organizerRole, setOrganizerRole] = useState("");
  const [profile, setProfile] = useState({ organization_name: "", mission: "", vision: "", people_served: "", programs: "", website: "", location: "", annual_budget: "", ein: "", fundraising_goal: "", currency: "USD" });
  const [audienceChoices, setAudienceChoices] = useState({});
  const [participation, setParticipation] = useState("");
  const [meeting, setMeeting] = useState({ date: "", time: "", timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "", notes: "" });
  const [meetingShareToken, setMeetingShareToken] = useState("");
  const [invite, setInvite] = useState({ name: "", email: "", role: "" });
  const [contributors, setContributors] = useState([]);
  const [finalStrategy, setFinalStrategy] = useState(null);
  const [soloDraft, setSoloDraft] = useState(null);
  const [strategyShareToken, setStrategyShareToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [draftDirty, setDraftDirty] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const draftSnapshot = useRef(null);
  const draftQueue = useRef(Promise.resolve());
  useEffect(() => {
    if (!draftDirty || !["welcome", "questions", "participation"].includes(phase)) return;
    const body = { profile, organizer_role: organizerRole, question, answer, participation, phase };
    draftSnapshot.current = JSON.stringify(body);
    const timer = setTimeout(async () => {
      setSaveStatus("Saving your unfinished answer…");
      try {
        draftQueue.current = draftQueue.current.catch(() => {}).then(() => axios.put(API + "/strategy/" + token + "/questionnaire-draft", body, { headers: authHeaders() }));
        await draftQueue.current;
        if (draftSnapshot.current === JSON.stringify(body)) { setDraftDirty(false); setSaveStatus("Your work is saved. You can leave and return to this step."); }
      } catch (_) { setSaveStatus("This edit has not saved yet. Keep this page open and retry before leaving."); }
    }, 650);
    return () => clearTimeout(timer);
  }, [draftDirty, phase, profile, organizerRole, question, answer, participation, token]);

  const chooseNextPhase = (data) => {
    if (data.final_strategy && data.strategy_adopted) { setFinalStrategy(data.final_strategy); setStrategyShareToken(data.strategy_share_token || ""); setPhase("complete"); return; }
    if (data.professional_review_required && data.professional_review_status === "pending") { setPhase("submitted"); return; }
    if (data.strategy_mode === "solo" && data.strategy_draft) { setSoloDraft(data.strategy_draft); setPhase("solo_draft"); return; }
    if (!String(data.organizer_role || "").trim() || !data.organization_profile?.mission) { setPhase("welcome"); return; }

    const answers = data.strategy_answers || {};
    const choices = data.audience_choices || {};
    const individualMissing = STRATEGY_QUESTIONS.slice(0, 5).find((q) => !String(answers[q.key] || "").trim());
    if (individualMissing) { setQuestion(Number(individualMissing.key)); setAnswer(data.questionnaire_draft?.answers?.[individualMissing.key] || ""); setPhase("questions"); return; }

    if (typeof choices.businesses !== "boolean") { setPhase("business_choice"); return; }
    if (choices.businesses) {
      const businessMissing = STRATEGY_QUESTIONS.slice(5, 10).find((q) => !String(answers[q.key] || "").trim());
      if (businessMissing) { setQuestion(Number(businessMissing.key)); setAnswer(data.questionnaire_draft?.answers?.[businessMissing.key] || ""); setPhase("questions"); return; }
    }

    if (typeof choices.grantors !== "boolean") { setPhase("grantor_choice"); return; }
    if (choices.grantors) {
      const grantMissing = STRATEGY_QUESTIONS.slice(10, 15).find((q) => !String(answers[q.key] || "").trim());
      if (grantMissing) { setQuestion(Number(grantMissing.key)); setAnswer(data.questionnaire_draft?.answers?.[grantMissing.key] || ""); setPhase("questions"); return; }
    }

    if (!String(data.participation || "").trim()) { setPhase("participation"); return; }
    if (!(data.meeting || {}).date || !(data.meeting || {}).time) { setPhase("collaboration_choice"); return; }
    setPhase("planning");
  };

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/strategy/${token}`, { headers: authHeaders() });
      setLead(data);
      setOrganizerRole(data.organizer_role || data.questionnaire_draft?.organizer_role || "");
      setProfile({ organization_name: data.organization_name || "", mission: "", vision: "", people_served: "", programs: "", website: "", location: "", annual_budget: "", ein: "", fundraising_goal: "", currency: "USD", ...(data.organization_profile || {}), ...(!data.organization_profile?.mission ? data.questionnaire_draft?.profile || {} : {}) });
      setAudienceChoices(data.audience_choices || {});
      setParticipation(data.participation || data.questionnaire_draft?.participation || "");
      setMeeting({ date: "", time: "", timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "", notes: "", ...(data.meeting || {}) });
      setMeetingShareToken(data.meeting_share_token || "");
      setContributors(data.contributors || []);
      setFinalStrategy(data.final_strategy || null);
      setStrategyShareToken(data.strategy_share_token || "");
      chooseNextPhase(data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate(`/login?resume_token=${encodeURIComponent(token)}`, { replace: true });
        return;
      }
      setError(errorMessage(err, "We could not open this strategy right now."));
      setPhase("error");
    }
  };

  useEffect(() => { document.title = "Build Your Fundraising Strategy | Auto Fundraiser"; load(); }, [token]);

  useEffect(() => {
    if (phase !== "planning") return;
    const timer = setInterval(() => {
      axios.get(`${API}/strategy/${token}`, { headers: authHeaders() }).then(({ data }) => {
        setContributors(data.contributors || []);
      }).catch(() => {});
    }, 5000);
    return () => clearInterval(timer);
  }, [phase, token]);

  const saveOrganizerRole = async () => {
    if (!organizerRole.trim()) return;
    setDraftDirty(false); setBusy(true); setError("");
    try {
      await axios.put(`${API}/strategy/${token}/organization-profile`, { ...profile, annual_budget: profile.annual_budget === "" || profile.annual_budget == null ? null : Number(profile.annual_budget), fundraising_goal: Number(profile.fundraising_goal) }, { headers: authHeaders() });
      await axios.put(`${API}/strategy/${token}/organizer-role`, { role: organizerRole.trim() }, { headers: authHeaders() });
      await load(); window.scrollTo({ top: 0 });
    } catch (err) { setError(errorMessage(err, "We could not save your role.")); }
    finally { setBusy(false); }
  };

  const saveAnswer = async () => {
    if (!answer.trim()) return;
    setDraftDirty(false); setBusy(true); setError("");
    try {
      await axios.put(`${API}/strategy/${token}/answer`, { question, answer }, { headers: authHeaders() });
      const nextAnswers = { ...(lead?.strategy_answers || {}), [String(question)]: answer.trim() };
      setLead((current) => ({ ...current, strategy_answers: nextAnswers }));
      setAnswer("");
      if (question < 5) { setQuestion(question + 1); }
      else if (question === 5) { setPhase("business_choice"); }
      else if (question < 10) { setQuestion(question + 1); }
      else if (question === 10) { setPhase("grantor_choice"); }
      else if (question < 15) { setQuestion(question + 1); }
      else { setPhase("participation"); }
      window.scrollTo({ top: 0 });
    } catch (err) { setError(errorMessage(err, "We could not save your answer.")); }
    finally { setBusy(false); }
  };

  const chooseAudience = async (audience, pursue) => {
    setBusy(true); setError("");
    try {
      await axios.put(`${API}/strategy/${token}/audience-choice`, { audience, pursue }, { headers: authHeaders() });
      const nextChoices = { ...audienceChoices, [audience]: pursue };
      setAudienceChoices(nextChoices);
      setLead((current) => ({ ...current, audience_choices: nextChoices }));
      if (audience === "businesses") {
        if (pursue) { setQuestion(6); setPhase("questions"); }
        else setPhase("grantor_choice");
      } else {
        if (pursue) { setQuestion(11); setPhase("questions"); }
        else setPhase("participation");
      }
      window.scrollTo({ top: 0 });
    } catch (err) { setError(errorMessage(err, "We could not save that choice.")); }
    finally { setBusy(false); }
  };

  const saveParticipation = async () => {
    if (!participation.trim()) return;
    setBusy(true); setError("");
    try {
      await axios.put(`${API}/strategy/${token}/participation`, { answer: participation }, { headers: authHeaders() });
      setLead((current) => ({ ...current, participation: participation.trim() }));
      setPhase("collaboration_choice"); window.scrollTo({ top: 0 });
    } catch (err) { setError(errorMessage(err, "We could not save your participation answer.")); }
    finally { setBusy(false); }
  };

  const saveMeeting = async () => {
    if (!meeting.date || !meeting.time) return;
    setBusy(true); setError("");
    try {
      const { data } = await axios.put(`${API}/strategy/${token}/meeting`, meeting, { headers: authHeaders() });
      setMeetingShareToken(data.meeting_share_token || meetingShareToken);
      setLead((current) => ({ ...current, meeting }));
      setPhase("planning"); window.scrollTo({ top: 0 });
    } catch (err) { setError(errorMessage(err, "We could not save the meeting details.")); }
    finally { setBusy(false); }
  };

  const buildSolo = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await postGeneration(`${API}/strategy/${token}/build-solo`, {}, { headers: authHeaders() });
      if (data.professional_review_required) { await load(); window.scrollTo({ top: 0 }); return; }
      setSoloDraft(data.strategy_draft);
      setPhase("solo_draft");
      window.scrollTo({ top: 0 });
    } catch (err) {
      setError(errorMessage(err, "We could not build your final strategy."));
    } finally {
      setBusy(false);
    }
  };

  const adoptSolo = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/meeting/${token}/finalize`, {}, { headers: authHeaders() });
      setFinalStrategy(data.final_strategy); setStrategyShareToken(data.strategy_share_token || "");
      setLead((current) => ({ ...current, booking_url: data.booking_url, strategy_branding: data.strategy_branding }));
      setPhase("complete"); window.scrollTo({ top: 0 });
    } catch (err) { setError(errorMessage(err, "We could not adopt your strategy yet. Please try again.")); }
    finally { setBusy(false); }
  };

  const inviteContributor = async () => {
    if (!invite.name.trim() || !invite.email.trim()) return;
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/strategy/${token}/contributors`, invite, { headers: authHeaders() });
      setContributors((current) => current.some((item) => item.token === data.token) ? current : [...current, data]);
      setInvite({ name: "", email: "", role: "" });
      if (!data.invitation_sent && !data.is_admin_test) setError(`The invitation is saved, but the email did not send. Share this contribution link with ${data.name}: ${data.invite_url}`);
    } catch (err) { setError(errorMessage(err, "We could not send that invitation.")); }
    finally { setBusy(false); }
  };

  if (phase === "loading") return <Shell narrow><Spinner label="Opening your strategy builder…" /></Shell>;
  if (phase === "submitted") return <Shell narrow><StrategySubmissionComplete name={lead?.name} organization={lead?.organization_name} /></Shell>;
  if (phase === "error") return <Shell narrow><div className="af-message-card"><h1>We couldn't open this strategy right now.</h1><p>{error}</p><button className="af-primary af-primary-wide" onClick={load}>TRY AGAIN</button></div></Shell>;
  if (phase === "complete" && finalStrategy) {
    const downloadUrl = strategyShareToken ? `${API}/share/strategy/${strategyShareToken}/download` : "";
    return <Shell><section className="af-meeting-hero"><div className="af-kicker">YOUR FUNDRAISING STRATEGY IS READY</div><p>{deliveryMessage(finalStrategy)}</p></section><StrategyView strategy={finalStrategy} downloadUrl={downloadUrl} branding={lead?.strategy_branding} bookingUrl={lead?.booking_url} /><StrategyNextSteps token={token} headers={authHeaders()} />{lead?.workflow?.board_portfolios && <BoardPortfolioManager token={token} />}</Shell>;
  }

  if (phase === "solo_draft") return <Shell>
    <section className="af-meeting-hero"><div className="af-kicker">YOUR FUNDRAISING STRATEGY DRAFT IS READY</div><h1>Read your strategy and make sure it is the direction you want.</h1><p>Your answers have helped shape this draft around your mission and ideas. Read the recommendations carefully and consider how they fit the work you want your organization to accomplish. When you are happy with the direction, adopt it and download your final strategy.</p></section>
    <StrategyView strategy={soloDraft} draft />
    <section className="af-adopt-strategy"><button className="af-primary af-primary-wide" disabled={busy} onClick={adoptSolo}>{busy ? "ADOPTING…" : "ADOPT MY FUNDRAISING STRATEGY"}</button>
      <button className="af-secondary af-primary-wide" disabled={busy} onClick={() => { setQuestion(1); setAnswer(lead?.strategy_answers?.["1"] || ""); setPhase("questions"); }}>CHANGE MY ANSWERS</button>
      {error && <p className="af-error">{error}</p>}
    </section>
  </Shell>;

  if (phase === "welcome") return <Shell narrow>
    <section className="af-work-card af-question-work af-welcome-builder">
      <div className="af-kicker">WELCOME TO YOUR FUNDRAISING STRATEGY BUILDER</div>
      <h1>{firstName(lead?.name) ? `Welcome, ${firstName(lead.name)}.` : "Welcome to your fundraising workspace."}</h1>
      <p>You've taken an important step towards building a fundraising strategy {lead?.organization_name || "your organization"} can confidently work with. Now let's bring together what we know about your mission and develop a clear direction for raising the money your organization needs.</p>
      <p>Your organization has its own mission, strengths and funding opportunities. Your answers will help us identify exactly the types of people, businesses and grantors with the strongest reasons to support your work, and shape the relationships and asks your organization will eventually put into practice. We will work through one question at a time, and your answers save as you go.</p>
      <p>Start with individuals. Include businesses and grantors if they fit your mission. You can build on your own or bring your board's ideas together.</p>
      <label className="af-full-label">Before we start, what is your role in {lead?.organization_name || "the organization"}?
        <input value={organizerRole} onChange={(e) => { setDraftDirty(true); setSaveStatus("Saving your edits…"); setOrganizerRole(e.target.value); }} placeholder="Founder, Executive Director, Board Chair, Trustee…" />
      </label>
      <div className="af-profile-form">
        <label>Organization name<input value={profile.organization_name} onChange={(e) => { setDraftDirty(true); setSaveStatus("Saving your edits…"); setProfile({ ...profile, organization_name: e.target.value }); }} /></label>
        <label>Mission statement<textarea rows={3} value={profile.mission} onChange={(e) => { setDraftDirty(true); setSaveStatus("Saving your edits…"); setProfile({ ...profile, mission: e.target.value }); }} /></label>
        <label>Vision for your organization (optional)<textarea rows={2} value={profile.vision} onChange={(e) => { setDraftDirty(true); setSaveStatus("Saving your edits…"); setProfile({ ...profile, vision: e.target.value }); }} /></label>
        <label>Who does your organization serve?<textarea rows={2} value={profile.people_served} onChange={(e) => { setDraftDirty(true); setSaveStatus("Saving your edits…"); setProfile({ ...profile, people_served: e.target.value }); }} /></label>
        <label>Your programs and the impact they create<textarea aria-label="Your programs and the impact they create" rows={4} value={profile.programs || ""} onChange={(e) => { setDraftDirty(true); setSaveStatus("Saving your edits…"); setProfile({ ...profile, programs: e.target.value }); }} placeholder="Describe your main programs, who takes part, what changes for them and any real costs or results you can share." /></label>
        <label>Organization website (optional)<input type="url" placeholder="https://yourorganization.org" value={profile.website} onChange={(e) => { setDraftDirty(true); setSaveStatus("Saving your edits…"); setProfile({ ...profile, website: e.target.value }); }} /></label>
        <label>Organization location (optional)<input placeholder="City, state or region, country" value={profile.location} onChange={(e) => { setDraftDirty(true); setSaveStatus("Saving your edits…"); setProfile({ ...profile, location: e.target.value }); }} /></label>
        <label>Annual operating budget (optional)<input type="number" min="0" value={profile.annual_budget ?? ""} onChange={(e) => { setDraftDirty(true); setSaveStatus("Saving your edits…"); setProfile({ ...profile, annual_budget: e.target.value }); }} /><small>Use the currency selected below. Leave blank if you are not sure.</small></label>
        <label>How much are you trying to raise?<input type="number" min="1" value={profile.fundraising_goal} onChange={(e) => { setDraftDirty(true); setSaveStatus("Saving your edits…"); setProfile({ ...profile, fundraising_goal: e.target.value }); }} /></label>
        <label>Fundraising currency<select value={profile.currency} onChange={(e) => { setDraftDirty(true); setSaveStatus("Saving your edits…"); setProfile({ ...profile, currency: e.target.value }); }}>{["USD", "GBP", "EUR", "CAD", "AUD", "NGN"].map((currency) => <option key={currency}>{currency}</option>)}</select></label>
      </div>
      {error && <p className="af-error">{error}</p>}
      {saveStatus && <p role="status">{saveStatus}</p>}
      <button className="af-primary af-primary-wide" disabled={busy || !organizerRole.trim() || !profile.mission.trim() || !profile.people_served.trim() || Number(profile.fundraising_goal) <= 0 || !profile.fundraising_goal} onClick={saveOrganizerRole}>{busy ? "SAVING…" : "START BUILDING MY STRATEGY"} <span>→</span></button>
    </section>
  </Shell>;

  if (phase === "questions") {
    const q = STRATEGY_QUESTIONS[question - 1];
    return <Shell narrow>
      <div className="af-kicker">BUILD YOUR STRATEGY • {q.audience.toUpperCase()} • {q.step}</div>
      <section className="af-work-card af-question-work">
        <h1>{q.title}</h1>
        <p>{q.hint}</p>
        {strategyProgress(question, lead?.strategy_answers) && <p className="af-journey-note">{strategyProgress(question, lead.strategy_answers)}</p>}
        <textarea rows={7} disabled={busy} value={answer} onChange={(e) => { setDraftDirty(true); setSaveStatus("Saving your edits…"); setAnswer(e.target.value); }} placeholder="Tell us what you think, in your own words…" />
        {saveStatus && <p role="status">{saveStatus}</p>}
        {error && <p className="af-error">{error}</p>}
        <button className="af-primary af-primary-wide" disabled={busy || !answer.trim()} onClick={saveAnswer}>{busy ? "SAVING…" : "CONTINUE"} <span>→</span></button>
      </section>
    </Shell>;
  }

  if (phase === "business_choice") return <Shell narrow>
    <section className="af-work-card af-question-work">
      <div className="af-kicker">BUSINESS FUNDRAISING</div>
      <h1>Are there types of businesses that have a strong reason to support your mission?</h1>
      <p>If yes, we will map that business fundraising journey separately so you know which businesses to focus on, where to find them, how to attract them, how to build the relationship and what to ask them for. If you have no ideas yet, choose No. We will assess suitable options when building your strategy.</p>
      <div className="af-binary">
        <button disabled={busy} onClick={() => chooseAudience("businesses", true)}>YES, THERE ARE</button>
        <button disabled={busy} onClick={() => chooseAudience("businesses", false)}>NO, NOT RIGHT NOW</button>
      </div>
      {error && <p className="af-error">{error}</p>}
    </section>
  </Shell>;

  if (phase === "grantor_choice") return <Shell narrow>
    <section className="af-work-card af-question-work">
      <div className="af-kicker">GRANT FUNDRAISING</div>
      <h1>Are there types of grantors that have a strong reason to fund your mission?</h1>
      <p>If yes, we will map the grantor journey separately, from the funders that fit your work to where to find them, how to build credibility and relationships, how to move toward funding and what to ask them for. If you have no ideas yet, choose No. We will assess suitable options when building your strategy.</p>
      <div className="af-binary">
        <button disabled={busy} onClick={() => chooseAudience("grantors", true)}>YES, THERE ARE</button>
        <button disabled={busy} onClick={() => chooseAudience("grantors", false)}>NO, NOT RIGHT NOW</button>
      </div>
      {error && <p className="af-error">{error}</p>}
    </section>
  </Shell>;

  if (phase === "participation") return <Shell narrow>
    <div className="af-kicker">YOUR PARTICIPATION</div>
    <section className="af-work-card af-question-work">
      <h1>How would you personally like to participate in fundraising?</h1>
      <p>Tell us the role you are comfortable playing. Your responsibility will be included in the fundraising strategy so everyone knows how they can help.</p>
      <textarea rows={7} value={participation} onChange={(e) => { setDraftDirty(true); setSaveStatus("Saving your edits…"); setParticipation(e.target.value); }} placeholder="I would be comfortable helping by…" />
      {error && <p className="af-error">{error}</p>}
      <button className="af-primary af-primary-wide" disabled={busy || !participation.trim()} onClick={saveParticipation}>{busy ? "SAVING…" : "COMPLETE MY ANSWERS"} <span>→</span></button>
    </section>
  </Shell>;

  if (phase === "collaboration_choice") return <Shell narrow>
    <section className="af-work-card af-question-work af-complete-answers">
      <div className="af-kicker">YOUR ANSWERS ARE COMPLETE</div>
      <h1>You've shared the ideas that will help shape your fundraising strategy.</h1>
      <p>Your answers are saved. Your strategy becomes stronger when the people helping you execute it understand the direction and have an opportunity to contribute. Invite your board, staff or supporters to share their ideas, or continue with the information you have provided on your own.</p>
      <h2>Do you want other people in your organization to contribute before the strategy is finalized?</h2>
      <div className="af-collaboration-actions">
        <button className="af-primary af-primary-wide" onClick={() => setPhase("meeting_setup")}>YES, BRING MY PEOPLE IN <span>→</span></button>
        <button className="af-secondary af-primary-wide" disabled={busy} onClick={buildSolo}>{busy ? "BUILDING…" : "NO, BUILD IT FROM MY ANSWERS"}</button>
      </div>
      {busy && <p role="status">Your answers are saved and your strategy is being prepared. You can close this page; we will email you when it is ready.</p>}
      {error && <p className="af-error">{error}</p>}
    </section>
  </Shell>;

  if (phase === "meeting_setup") return <Shell narrow>
    <section className="af-work-card af-question-work">
      <div className="af-kicker">SET YOUR GROUP FUNDRAISING MEETING</div>
      <h1>When will everyone come together to agree on the strategy?</h1>
      <p>Set the date and time before you invite people. We will include the meeting details in their invitation, keep their responses together in your workspace and remind you before the meeting.</p>
      <div className="af-fields-three">
        <label>Date<input type="date" value={meeting.date || ""} onChange={(e) => setMeeting((current) => ({ ...current, date: e.target.value }))} /></label>
        <label>Time<input type="time" value={meeting.time || ""} onChange={(e) => setMeeting((current) => ({ ...current, time: e.target.value }))} /></label>
        <label>Timezone<input value={meeting.timezone || ""} onChange={(e) => setMeeting((current) => ({ ...current, timezone: e.target.value }))} /></label>
      </div>
      <label className="af-full-label">Anything people should know before the meeting<textarea rows={3} value={meeting.notes || ""} onChange={(e) => setMeeting((current) => ({ ...current, notes: e.target.value }))} /></label>
      {error && <p className="af-error">{error}</p>}
      <button className="af-primary af-primary-wide" disabled={busy || !meeting.date || !meeting.time} onClick={saveMeeting}>{busy ? "SAVING…" : "SAVE MEETING & INVITE PEOPLE"} <span>→</span></button>
    </section>
  </Shell>;

  return <Shell>
    <section className="af-planning-hero">
      <div>
        <div className="af-kicker">YOUR FUNDRAISING STRATEGY WORKSPACE</div>
        <h1>Your answers are in. Now bring the people who will help execute the strategy into building it.</h1>
        <p>Your meeting is set for <strong>{meeting.date}</strong>{meeting.time ? <> at <strong>{meeting.time}</strong></> : null}. Invite the people whose ideas should be in the room. Every completed contribution will appear here before the meeting.</p>
      </div>
      <div className="af-status-pill">PAID STRATEGY BUILDER</div>
    </section>

    {meetingShareToken && <section className="af-share-game-card">
      <strong>Group game participant link</strong>
      <p>Share this during the meeting so everyone can follow the agreed strategy as it develops.</p>
      <div className="af-share-link-row"><input aria-label="Group participant link" readOnly value={`${window.location.origin}/game/${meetingShareToken}`} /><CopyLinkButton value={`${window.location.origin}/game/${meetingShareToken}`} /></div>
    </section>}

    <section className="af-work-card">
      <span className="af-card-label">INVITE CONTRIBUTORS</span>
      <h2>Who else should help shape the strategy?</h2>
      <p>Invite the people who can help shape and carry out your organization's strategy. They will work through the same fundraising framework and share the roles they would be comfortable playing, so your team can agree on a direction together.</p>
      <div className="af-fields-three">
        <label>Name<input value={invite.name} onChange={(e) => setInvite((current) => ({ ...current, name: e.target.value }))} /></label>
        <label>Email<input type="email" value={invite.email} onChange={(e) => setInvite((current) => ({ ...current, email: e.target.value }))} /></label>
        <label>Role<input value={invite.role} onChange={(e) => setInvite((current) => ({ ...current, role: e.target.value }))} placeholder="Board member, staff…" /></label>
      </div>
      <button className="af-primary" disabled={busy || !invite.name.trim() || !invite.email.trim()} onClick={inviteContributor}>SEND INVITATION</button>
      <div className="af-contributor-list">
        {contributors.length === 0 ? <p className="af-muted">No contributors invited yet.</p> : contributors.map((person) => <div key={person.token} className="af-contributor-response">
          <div className="af-contributor-row"><div><strong>{person.name}</strong><span>{person.role || person.email}</span></div><span className={person.completed ? "af-complete" : "af-waiting"}>{person.completed ? "COMPLETED" : "INVITED"}</span></div>
          {!person.completed && <div className="af-invitation-status"><p>{lead.is_admin_test ? "Test contribution link ready. No email was sent." : person.invitation?.status === "accepted" ? "Invitation submitted by email." : "The invitation is saved. Share the link or retry the email."}</p>
            <div className="af-share-link-row"><input aria-label={`${person.name}'s invitation link`} readOnly value={`${window.location.origin}/contribute/${person.token}`} /><CopyLinkButton label="COPY INVITATION LINK" value={`${window.location.origin}/contribute/${person.token}`} /></div>
            {!lead.is_admin_test && person.invitation?.status !== "accepted" && <button className="af-secondary" disabled={busy} onClick={async () => {
              setBusy(true); setError("");
              try { const { data } = await axios.post(`${API}/strategy/${token}/contributors/${person.token}/retry-invitation`, {}, { headers: authHeaders() });
                setContributors((current) => current.map((item) => item.token === person.token ? { ...item, ...data } : item));
                if (!data.invitation_sent) setError("The invitation is saved, but the email has not been submitted yet. You can share the link.");
              } catch (err) { setError(errorMessage(err, "We could not retry the invitation email.")); }
              finally { setBusy(false); }
            }}>RETRY INVITATION EMAIL</button>}
          </div>}
          {person.completed && person.email_notifications && <p>{lead.is_admin_test ? "Test contribution saved. Emails are skipped." : person.email_notifications.owner?.status === "accepted" ? "Your contribution notification has been submitted by email." : "The contribution is saved here. Your email notification is pending or failed; we will retry automatically."}</p>}
          {!lead.is_admin_test && person.completed && Object.values(person.email_notifications || {}).some((item) => item.status !== "accepted") && <button className="af-secondary" disabled={busy} onClick={async () => {
            setBusy(true); setError("");
            try {
              const { data } = await axios.post(`${API}/strategy/${token}/contributors/${person.token}/retry-emails`, {}, { headers: authHeaders() });
              setContributors((current) => current.map((item) => item.token === person.token ? { ...item, email_notifications: data.email_notifications } : item));
            } catch { setError("The contribution is saved. We could not retry these emails yet."); }
            finally { setBusy(false); }
          }}>RETRY CONTRIBUTION EMAILS</button>}
          {person.completed && <details>
            <summary>VIEW {person.name.toUpperCase()}'S IDEAS</summary>
            <div className="af-contributor-answers">
              {STRATEGY_QUESTIONS.filter((q) => person.answers?.[q.key]).map((q) => <article key={q.key}>
                <strong>{q.audience} · {q.step}</strong>
                <p>{person.answers[q.key]}</p>
              </article>)}
              <article><strong>HOW {person.name.toUpperCase()} WANTS TO PARTICIPATE</strong><p>{person.participation}</p></article>
            </div>
          </details>}
        </div>)}
      </div>
    </section>

    <section className="af-next-action">
      <div>
        <span className="af-card-label">GROUP FUNDRAISING MEETING</span>
        <h2>When the meeting starts, open the game and build the strategy together.</h2>
        <p>You will review everyone's ideas audience by audience, agree on the strongest answers, discuss how each person will participate and use the meeting transcript together with the adopted ideas to build the strategy draft.</p>
      </div>
      <button className="af-primary" onClick={() => navigate(`/meeting/${token}`)}>OPEN GROUP FUNDRAISING GAME <span>→</span></button>
    </section>
    {error && <p className="af-error">{error}</p>}
  </Shell>;
}

export function AutoFundraiserContributorPage() {
  const { token } = useParams();
  const [state, setState] = useState(null);
  const [phase, setPhase] = useState("questions");
  const [question, setQuestion] = useState(1);
  const [answers, setAnswers] = useState({});
  const [audienceChoices, setAudienceChoices] = useState({});
  const [participation, setParticipation] = useState("");
  const [role, setRole] = useState("");
  const [done, setDone] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/contribute/${token}`);
      setState(data);
      const contributor = data.contributor || {};
      setEmailNotifications(contributor.email_notifications || {});
      const savedAnswers = contributor.answers || {};
      const savedChoices = contributor.audience_choices || {};
      setAnswers(savedAnswers);
      setAudienceChoices(savedChoices);
      setParticipation(contributor.participation || "");
      setRole(contributor.role || "");
      if (contributor.completed) { setDone(true); return; }

      const individualMissing = STRATEGY_QUESTIONS.slice(0, 5).find((q) => !String(savedAnswers[q.key] || "").trim());
      if (individualMissing) { setQuestion(Number(individualMissing.key)); setPhase("questions"); return; }
      if (typeof savedChoices.businesses !== "boolean") { setPhase("business_choice"); return; }
      if (savedChoices.businesses) {
        const businessMissing = STRATEGY_QUESTIONS.slice(5, 10).find((q) => !String(savedAnswers[q.key] || "").trim());
        if (businessMissing) { setQuestion(Number(businessMissing.key)); setPhase("questions"); return; }
      }
      if (typeof savedChoices.grantors !== "boolean") { setPhase("grantor_choice"); return; }
      if (savedChoices.grantors) {
        const grantMissing = STRATEGY_QUESTIONS.slice(10, 15).find((q) => !String(savedAnswers[q.key] || "").trim());
        if (grantMissing) { setQuestion(Number(grantMissing.key)); setPhase("questions"); return; }
      }
      setPhase("participation");
    } catch (err) { setError(errorMessage(err, "We could not open this contribution form.")); }
  };

  useEffect(() => { load(); }, [token]);

  const saveContributionDraft = async (nextChoices = audienceChoices) => {
    setBusy(true); setError("");
    try {
      await axios.put(`${API}/contribute/${token}/draft`, { answers, audience_choices: nextChoices, participation, role });
      return true;
    } catch (err) { setError(errorMessage(err, "We could not save your answer yet. Please try again.")); return false; }
    finally { setBusy(false); }
  };

  const saveCurrentAnswer = async () => {
    const value = String(answers[String(question)] || "").trim();
    if (!value || busy || !await saveContributionDraft()) return;
    if (question < 5) setQuestion(question + 1);
    else if (question === 5) setPhase("business_choice");
    else if (question < 10) setQuestion(question + 1);
    else if (question === 10) setPhase("grantor_choice");
    else if (question < 15) setQuestion(question + 1);
    else setPhase("participation");
    window.scrollTo({ top: 0 });
  };

  const chooseAudience = async (audience, pursue) => {
    const next = { ...audienceChoices, [audience]: pursue };
    if (busy || !await saveContributionDraft(next)) return;
    setAudienceChoices(next);
    if (audience === "businesses") {
      if (pursue) { setQuestion(6); setPhase("questions"); }
      else setPhase("grantor_choice");
    } else {
      if (pursue) { setQuestion(11); setPhase("questions"); }
      else setPhase("participation");
    }
    window.scrollTo({ top: 0 });
  };

  const submit = async () => {
    if (!role.trim() || !participation.trim()) return;
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/contribute/${token}`, { answers, audience_choices: audienceChoices, participation, role });
      setEmailNotifications(data.email_notifications || {});
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch (err) {
      setError(errorMessage(err, "We could not save your contribution."));
    } finally {
      setBusy(false);
    }
  };

  if (!state && !error) return <Shell narrow><Spinner label="Opening your fundraising contribution form…" /></Shell>;
  if (error && !state) return <Shell narrow><div className="af-message-card"><h1>We couldn't open this contribution form.</h1><p>{error}</p></div></Shell>;
  if (done) return <Shell narrow><div className="af-message-card"><div className="af-kicker">YOUR IDEAS ARE IN</div><h1>{firstName(state.contributor?.name) ? `Thank you, ${firstName(state.contributor.name)}. Your contribution has been saved.` : "Thank you. Your contribution has been saved."}</h1><p>Your ideas have been submitted and will be considered during {state.organization_name}'s group fundraising meeting. Your participation is helping your team work towards a clearer fundraising direction.</p>
    <p>{state.is_admin_test ? "Your test ideas are saved. No emails were sent." : emailNotifications.contributor?.status === "accepted" ? "Your confirmation email has been submitted. Check your inbox and spam folder for your saved ideas." : "Your ideas are safely saved, but your confirmation email has not been submitted yet."}</p>
    {!state.is_admin_test && Object.values(emailNotifications).some((item) => item.status !== "accepted") && <><p>We will retry automatically. You can also try again now without submitting your answers again.</p><button className="af-secondary" disabled={busy} onClick={async () => {
      setBusy(true); setError("");
      try { const { data } = await axios.post(`${API}/contribute/${token}/retry-emails`); setEmailNotifications(data.email_notifications || {}); }
      catch { setError("Your ideas are still saved. We could not retry the emails yet."); }
      finally { setBusy(false); }
    }}>{busy ? "RETRYING…" : "RETRY EMAILS"}</button></>}
    {error && <p className="af-error">{error}</p>}
  </div></Shell>;

  if (phase === "business_choice") return <Shell narrow>
    <section className="af-work-card af-question-work">
      <div className="af-kicker">{state.organization_name} • BUSINESS FUNDRAISING</div>
      <h1>Do you know types of businesses that have a strong reason to support this mission?</h1>
      <p>If yes, map the business audience separately so the group can decide whether it belongs in the final strategy.</p>
      {error && <p className="af-error">{error}</p>}
      <div className="af-binary">
        <button disabled={busy} onClick={() => chooseAudience("businesses", true)}>YES, I DO</button>
        <button disabled={busy} onClick={() => chooseAudience("businesses", false)}>NO, NOT RIGHT NOW</button>
      </div>
    </section>
  </Shell>;

  if (phase === "grantor_choice") return <Shell narrow>
    <section className="af-work-card af-question-work">
      <div className="af-kicker">{state.organization_name} • GRANT FUNDRAISING</div>
      <h1>Do you know types of grantors that have a strong reason to fund this mission?</h1>
      <p>If yes, map the grantor audience separately so the group can review the idea during the fundraising meeting.</p>
      {error && <p className="af-error">{error}</p>}
      <div className="af-binary">
        <button disabled={busy} onClick={() => chooseAudience("grantors", true)}>YES, I DO</button>
        <button disabled={busy} onClick={() => chooseAudience("grantors", false)}>NO, NOT RIGHT NOW</button>
      </div>
    </section>
  </Shell>;

  if (phase === "participation") return <Shell narrow>
    <section className="af-work-card af-question-work">
      <div className="af-kicker">{state.organization_name} • YOUR PARTICIPATION</div>
      <h1>How would you be comfortable supporting fundraising?</h1>
      <p>Tell the group the role you are willing to play. Your agreed responsibility will become part of the final fundraising strategy.</p>
      <label className="af-full-label">Your role
        <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="Board member, trustee, staff, volunteer…" />
      </label>
      <textarea rows={7} value={participation} onChange={(e) => setParticipation(e.target.value)} placeholder="I would be comfortable helping by…" />
      {error && <p className="af-error">{error}</p>}
      <button className="af-primary af-primary-wide" disabled={busy || !role.trim() || !participation.trim()} onClick={submit}>{busy ? "SAVING…" : "SUBMIT MY CONTRIBUTION"}</button>
    </section>
  </Shell>;

  const q = STRATEGY_QUESTIONS[question - 1];
  return <Shell narrow>
    <div className="af-kicker">{state.organization_name} • {q.audience.toUpperCase()} • {q.step}</div>
    {question === 1 && <section className="af-work-card af-contributor-context">
      <h2>{firstName(state.contributor?.name) ? `Welcome, ${firstName(state.contributor.name)}.` : `Help ${state.organization_name} build its fundraising strategy.`}</h2>
      <p>{state.organization_name} is working towards a fundraising strategy that can help it raise the money needed to accomplish its mission. Your experience and ideas can help strengthen that direction.</p>
      {state.organization_profile?.mission && <p>{state.organization_profile.mission}</p>}{state.organization_profile?.people_served && <p><strong>Who we serve:</strong> {state.organization_profile.people_served}</p>}
      {state.organization_profile?.fundraising_goal > 0 && <p><strong>Our fundraising goal:</strong> {state.organization_profile.currency} {Number(state.organization_profile.fundraising_goal).toLocaleString()}</p>}
      <p>As you answer, think about the types of individuals, businesses and grantors with the strongest reasons to support this mission, and how your team could develop those relationships.</p>
    </section>}
    <section className="af-work-card af-question-work">
      <h1>{q.title}</h1>
      <p>{q.hint}</p>
      {strategyProgress(question, answers) && <p className="af-journey-note">{strategyProgress(question, answers)}</p>}
      <textarea rows={7} value={answers[String(question)] || ""} onChange={(e) => setAnswers({ ...answers, [String(question)]: e.target.value })} placeholder="Share your idea in your own words…" />
      {error && <p className="af-error">{error}</p>}
      <button className="af-primary af-primary-wide" disabled={busy || !String(answers[String(question)] || "").trim()} onClick={saveCurrentAnswer}>CONTINUE <span>→</span></button>
    </section>
  </Shell>;
}

function BoardPortfolioManager({ token }) {
  const [items, setItems] = useState([]);
  const [strategyShareToken, setStrategyShareToken] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [leaderName, setLeaderName] = useState("");
  const [roles, setRoles] = useState({});
  const [names, setNames] = useState({});
  const [commitments, setCommitments] = useState({});
  const [generated, setGenerated] = useState({});
  const [busyKey, setBusyKey] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/strategy/${token}/portfolio-candidates`, { headers: authHeaders() });
      setItems(data.items || []);
      setStrategyShareToken(data.strategy_share_token || "");
      setOrganizationName(data.organization_name || "");
      setLeaderName(data.leader_name || "");
      const nextRoles = {};
      (data.items || []).forEach((person) => { nextRoles[person.token] = person.role || ""; });
      setRoles(nextRoles);
      setNames(Object.fromEntries((data.items || []).map((person) => [person.token, person.name || ""])));
      setCommitments(Object.fromEntries((data.items || []).map((person) => [person.token, person.participation || ""])));
    } catch (err) {
      setError(errorMessage(err, "We could not open the board portfolio manager."));
    }
  };

  useEffect(() => { load(); }, [token]);

  const saveRole = async (person) => {
    const role = String(roles[person.token] || "").trim();
    const name = String(names[person.token] || "").trim();
    const participation = String(commitments[person.token] || "").trim();
    if (!role || !name || !participation) return;
    setBusyKey(`role-${person.token}`); setError(""); setMessage("");
    try {
      const { data } = await axios.put(
        `${API}/strategy/${token}/contributors/${person.token}/role`,
        { name, role, participation },
        { headers: authHeaders() },
      );
      setItems((current) => current.map((item) => item.token === person.token ? { ...item, name, role, participation, is_board_role: data.is_board_role, portfolio_identity_confirmed: true, board_portfolio_token: "" } : item));
      setGenerated((current) => { const next = { ...current }; delete next[person.token]; return next; });
      setMessage(`${name}'s name and role have been confirmed.`);
    } catch (err) {
      setError(errorMessage(err, "We could not save that role."));
    } finally {
      setBusyKey("");
    }
  };

  const generatePortfolio = async (person) => {
    setBusyKey(`generate-${person.token}`); setError(""); setMessage("");
    try {
      const { data } = await postGeneration(
        `${API}/strategy/${token}/portfolios/${person.token}/generate`,
        {},
        { headers: authHeaders() },
      );
      const payload = {
        strategy_url: data.strategy_url,
        portfolio_url: data.portfolio_url,
        email_subject: data.email_subject,
        email_body: data.email_body,
      };
      setGenerated((current) => ({ ...current, [person.token]: payload }));
      const shareToken = String(data.strategy_url || "").split("/share/strategy/")[1] || "";
      if (shareToken) setStrategyShareToken(shareToken);
      setItems((current) => current.map((item) => item.token === person.token ? { ...item, board_portfolio_token: String(data.portfolio_url || "").split("/portfolio/")[1] || item.board_portfolio_token } : item));
      setMessage(`${person.name}'s board portfolio is ready.`);
    } catch (err) {
      setError(errorMessage(err, "We could not generate that board portfolio."));
    } finally {
      setBusyKey("");
    }
  };

  const emailCopyFor = (person) => {
    if (generated[person.token]) return generated[person.token];
    if (!person.board_portfolio_token || !strategyShareToken) return null;
    const origin = window.location.origin;
    const strategyUrl = `${origin}/share/strategy/${strategyShareToken}`;
    const portfolioUrl = `${origin}/portfolio/${person.board_portfolio_token}`;
    const first = (person.name || "there").split(" ")[0];
    return {
      strategy_url: strategyUrl,
      portfolio_url: portfolioUrl,
      email_subject: `${organizationName || "Our organization"}: your fundraising strategy and board portfolio`,
      email_body: `Hi ${first},\n\nWe've finished ${organizationName || "our organization's"} fundraising strategy. I am sharing the final strategy with you together with your board fundraising portfolio.\n\nFundraising strategy:\n${strategyUrl}\n\nYour board fundraising portfolio:\n${portfolioUrl}\n\nYour portfolio shows the role you agreed to play in supporting fundraising. The strategy shows who we are raising money from, where to find them, how to attract them, how we will move each relationship through Know, Like, Trust, Ask, Follow-up and Stewardship, and what we will ask for.\n\nBest,\n${leaderName || organizationName || ""}`,
    };
  };

  const copyEmail = async (person) => {
    const draft = emailCopyFor(person);
    if (!draft) return;
    try {
      await navigator.clipboard.writeText(`Subject: ${draft.email_subject}\n\n${draft.email_body}`);
      setMessage(`Email for ${person.name} copied. You can paste it into your own email.`);
    } catch {
      setError("We could not copy the email automatically. You can still select and copy it manually.");
    }
  };

  const sendEmail = async (person) => {
    setBusyKey(`send-${person.token}`); setError(""); setMessage("");
    try {
      await axios.post(`${API}/strategy/${token}/portfolios/${person.token}/send`, {}, { headers: authHeaders() });
      setMessage(`Sent to ${person.email}.`);
    } catch (err) {
      setError(errorMessage(err, "We could not send that email."));
    } finally {
      setBusyKey("");
    }
  };

  if (!items.length && !error) return null;

  return <section className="af-portfolio-manager">
    <div className="af-kicker">BOARD FUNDRAISING PORTFOLIOS</div>
    <h2>Confirm who is on the board, then generate what you want to send.</h2>
    <p>Check each person's name and role, then confirm them. Each board member's portfolio will explain the fundraising responsibility they agreed to carry, the first actions they can take, and the support they need. When it is ready, you can send their strategy and portfolio together or copy the email into your own mailbox.</p>
    {message && <p className="af-success-text">{message}</p>}
    {error && <p className="af-error">{error}</p>}
    <div className="af-portfolio-list">
      {items.map((person) => {
        const draft = emailCopyFor(person);
        const role = roles[person.token] ?? person.role ?? "";
        const name = names[person.token] ?? person.name ?? "";
        const commitment = commitments[person.token] ?? person.participation ?? "";
        const confirmed = person.portfolio_identity_confirmed && name.trim() === person.name && role.trim() === person.role && commitment.trim() === person.participation;
        const looksBoard = /board|trustee/i.test(role) || (!/executive|program|development|finance|operations|staff|volunteer/i.test(role) && /director|chair|treasurer|secretary/i.test(role));
        return <article className="af-portfolio-person" key={person.token}>
          <div className="af-portfolio-person-head">
            <div><strong>{person.name}</strong><span>{person.email}</span></div>
            <span>{person.completed ? "COMPLETED" : "WAITING"}</span>
          </div>
          <label>Confirm this person's name
            <input value={name} onChange={(e) => setNames({ ...names, [person.token]: e.target.value })} />
          </label>
          <label>Confirm this person's role
            <input value={role} onChange={(e) => setRoles({ ...roles, [person.token]: e.target.value })} placeholder="Board member, trustee, staff, volunteer…" />
          </label>
          <label>Confirm this person's fundraising responsibility
            <textarea rows={4} value={commitment} onChange={(e) => setCommitments({ ...commitments, [person.token]: e.target.value })} />
          </label>
          <div className="af-row af-portfolio-actions">
            <button className="af-secondary" disabled={!!busyKey || !role.trim() || !name.trim() || !commitment.trim() || confirmed} onClick={() => saveRole(person)}>
              {busyKey === `role-${person.token}` ? "SAVING…" : confirmed ? "NAME & ROLE CONFIRMED" : "CONFIRM NAME & ROLE"}
            </button>
            {looksBoard && <button className="af-primary" disabled={!!busyKey || !confirmed} onClick={() => generatePortfolio(person)}>
              {busyKey === `generate-${person.token}` ? "GENERATING…" : person.board_portfolio_token || generated[person.token] ? "REGENERATE BOARD PORTFOLIO" : "GENERATE BOARD PORTFOLIO"}
            </button>}
          </div>
          {draft && confirmed && <div className="af-portfolio-ready">
            <strong>Portfolio ready</strong>
            <a href={draft.portfolio_url} target="_blank" rel="noreferrer">View board portfolio</a>
            <a href={draft.strategy_url} target="_blank" rel="noreferrer">View fundraising strategy</a>
            <div className="af-row">
              <button className="af-secondary" onClick={() => copyEmail(person)}>COPY EMAIL</button>
              <button className="af-primary" disabled={busyKey === `send-${person.token}`} onClick={() => sendEmail(person)}>
                {busyKey === `send-${person.token}` ? "SENDING…" : "SEND FROM PLATFORM"}
              </button>
            </div>
          </div>}
        </article>;
      })}
    </div>
  </section>;
}

function StrategyAudienceSection({ title, section }) {
  if (!section) return null;
  if (section.status === "not_pursued") return <article className="af-strategy-audience">
    <h2>{title}</h2>
    <p>This audience is not part of the fundraising strategy right now.</p>
  </article>;
  const rows = [
    ["Who", section.who],
    ["Where To Find Them", section.where_to_find],
    ["How To Attract Them", section.how_to_attract],
    ["How To Raise Money From Them", section.fundraising_process],
    ["What To Ask", section.ask],
  ];
  return <article className="af-strategy-audience">
    <h2>{title}</h2>
    {rows.map(([label, items]) => Array.isArray(items) && items.length ? <section key={label}>
      <h3>{label}</h3>
      <ul>{items.map((item, i) => <li key={i}>{typeof item === "string" ? item : JSON.stringify(item)}</li>)}</ul>
    </section> : null)}
  </article>;
}

export function StrategyView({ strategy, downloadUrl = "", draft = false, branding = {}, bookingUrl = "" }) {
  if (!strategy) return null;
  return <section className="af-final-strategy" style={{ "--af-plan-primary": readableBrandColor(branding.primary_color || "#0b6f61"), "--af-plan-accent": branding.accent_color || "#c4a765" }}>
    <div className="af-kicker">{draft ? "YOUR STRATEGY DRAFT" : "YOUR FINAL FUNDRAISING STRATEGY"}</div>
    <h1>{branding.cover_title || strategy.title}</h1>
    {branding.subtitle && <p>{branding.subtitle}</p>}
    <p className="af-final-summary">{strategy.executive_summary}</p>
    {downloadUrl && <div className="af-final-download"><a className="af-secondary af-link-button" href={downloadUrl}>DOWNLOAD FUNDRAISING STRATEGY</a></div>}

    <StrategyAudienceSection title="Individual Fundraising Strategy" section={strategy.individuals} />
    <StrategyAudienceSection title="Business Fundraising Strategy" section={strategy.businesses} />
    <StrategyAudienceSection title="Grant Fundraising Strategy" section={strategy.grantors} />

    {Array.isArray(strategy.participation) && strategy.participation.length > 0 && <article className="af-strategy-audience">
      <h2>How People Will Participate</h2>
      <div className="af-commitments">
        {strategy.participation.map((person, i) => <div key={i}>
          <strong>{person.name || "Participant"}{person.role ? ` · ${person.role}` : ""}</strong>
          <p>{person.commitment}</p>
        </div>)}
      </div>
    </article>}

    {!draft && bookingUrl && <section className="af-execution-cta">
      <h2>Discuss with a fundraising professional.</h2>
      <p>Your plan is ready before your meeting. Talk through your strategy, refine the next steps and leave knowing how to put it to work for your organization.</p>
      <a className="af-primary af-link-button" href={bookingUrl}>DISCUSS WITH A FUNDRAISING PROFESSIONAL</a>
    </section>}
  </section>;
}

export function AutoFundraiserSharedStrategyPage() {
  const { token } = useParams();
  const [state, setState] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    axios.get(`${API}/share/strategy/${token}`)
      .then(({ data }) => setState(data))
      .catch((err) => setError(errorMessage(err, "This fundraising strategy is not available.")));
  }, [token]);
  if (!state && !error) return <Shell><Spinner label="Opening the fundraising strategy…" /></Shell>;
  if (error) return <Shell narrow><div className="af-message-card"><h1>We couldn't open this strategy.</h1><p>{error}</p></div></Shell>;
  const downloadUrl = state.download_url ? `${process.env.REACT_APP_BACKEND_URL}${state.download_url}` : "";
  return <Shell><StrategyView strategy={state.final_strategy} downloadUrl={downloadUrl} branding={state.branding} bookingUrl={state.booking_url} /><a className="af-secondary af-link-button" href={`${API}/share/strategy/${token}/branded-plan`}>DOWNLOAD BRANDED PLAN</a></Shell>;
}

export function AutoFundraiserBoardPortfolioPage() {
  const { token } = useParams();
  const [state, setState] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    axios.get(`${API}/portfolio/${token}`)
      .then(({ data }) => setState(data))
      .catch((err) => setError(errorMessage(err, "This board portfolio is not available.")));
  }, [token]);
  if (!state && !error) return <Shell narrow><Spinner label="Opening your board fundraising portfolio…" /></Shell>;
  if (error) return <Shell narrow><div className="af-message-card"><h1>We couldn't open this board portfolio.</h1><p>{error}</p></div></Shell>;
  const p = state.portfolio;
  const responsibilities = Array.isArray(p.responsibilities) ? p.responsibilities : [];
  const listSection = (title, items) => Array.isArray(items) && items.length ? <div className="af-portfolio-detail">
    <h2>{title}</h2>
    <ul>{items.map((item, index) => <li key={index}>{typeof item === "string" ? item : (item.action || JSON.stringify(item))}</li>)}</ul>
  </div> : null;

  return <Shell narrow>
    <section className="af-board-portfolio">
      <div className="af-kicker">YOUR BOARD FUNDRAISING PORTFOLIO</div>
      <h1>{p.name}</h1>
      <p className="af-board-role">{p.role}</p>

      <div className="af-readback">
        <span>YOUR ROLE IN THIS FUNDRAISING STRATEGY</span>
        <p>{p.fundraising_role_summary || p.fundraising_participation}</p>
      </div>

      {Array.isArray(p.audience_focus) && p.audience_focus.length > 0 && <div className="af-portfolio-detail">
        <h2>Your Funding Audience Focus</h2>
        <ul>{p.audience_focus.map((item, index) => <li key={index}>{item}</li>)}</ul>
      </div>}

      {responsibilities.length > 0 && <div className="af-portfolio-detail">
        <h2>Your Agreed Fundraising Responsibilities</h2>
        {responsibilities.map((item, index) => <article key={index} className="af-portfolio-responsibility">
          <strong>{typeof item === "string" ? item : item.action}</strong>
          {typeof item !== "string" && item.when && <p><b>When:</b> {item.when}</p>}
          {typeof item !== "string" && item.strategy_connection && <p>{item.strategy_connection}</p>}
        </article>)}
      </div>}

      {listSection("Your First Actions", p.first_actions)}
      {listSection("What You Need To Do This Well", p.tools_and_support_needed)}
      {listSection("Your Accountability", p.accountability)}

      <div className="af-readback">
        <span>THE STRATEGY BEHIND YOUR PORTFOLIO</span>
        <p>Your responsibilities sit inside the strategy the group adopted: the right funding audiences, where to find them, how to attract them, how each relationship moves through Know, Like, Trust, Ask, Follow-up and Stewardship, and the exact ask the organization agreed to make.</p>
      </div>
      {state.strategy_url && <a className="af-primary af-primary-wide af-link-button" href={state.strategy_url}>VIEW THE FULL FUNDRAISING STRATEGY <span>→</span></a>}
    </section>
  </Shell>;
}

export function AutoFundraiserGameViewerPage() {
  const { token } = useParams();
  const [state, setState] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    const load = () => axios.get(`${API}/game/${token}`)
      .then(({ data }) => { if (active) { setState(data); setError(""); } })
      .catch((err) => { if (active) setError(errorMessage(err, "We could not open this group fundraising game.")); });
    load();
    const timer = setInterval(load, 5000);
    return () => { active = false; clearInterval(timer); };
  }, [token]);

  if (!state && !error) return <Shell><Spinner label="Opening the group fundraising game…" /></Shell>;
  if (error && !state) return <Shell narrow><div className="af-message-card"><h1>We couldn't open this group fundraising game.</h1><p>{error}</p></div></Shell>;
  if (state?.final_strategy) {
    const downloadUrl = state.strategy_share_token ? `${API}/share/strategy/${state.strategy_share_token}/download` : "";
    return <Shell><div className="af-kicker af-review-kicker">STRATEGY ADOPTED</div><StrategyView strategy={state.final_strategy} downloadUrl={downloadUrl} branding={state.strategy_branding} bookingUrl={state.booking_url} /></Shell>;
  }
  if (state?.strategy_draft) return <Shell>
    <section className="af-meeting-hero"><div className="af-kicker">YOUR STRATEGY DRAFT IS READY</div><h1>Review it together before you adopt it.</h1><p>This draft was built from the ideas the group selected and the discussion captured during the meeting.</p></section>
    <StrategyView strategy={state.strategy_draft} draft />
  </Shell>;

  const sourceIdeasFor = (key) => {
    const ideas = [];
    const organizer = state?.strategy_answers?.[key];
    if (organizer) ideas.push({ source: state.organizer_name || "Organizer", text: organizer });
    (state?.contributors || []).forEach((person) => {
      const text = person.answers?.[key];
      if (text) ideas.push({ source: person.name, text });
    });
    return ideas;
  };
  const hasBusiness = [6,7,8,9,10].some((n) => sourceIdeasFor(String(n)).length);
  const hasGrantors = [11,12,13,14,15].some((n) => sourceIdeasFor(String(n)).length);
  const visibleQuestions = STRATEGY_QUESTIONS.filter((q) => Number(q.key) <= 5 || (Number(q.key) <= 10 ? hasBusiness : hasGrantors));

  return <Shell>
    <section className="af-meeting-hero">
      <div className="af-kicker">LIVE GROUP FUNDRAISING GAME</div>
      <h1>{state.organization_name} is building its fundraising strategy together.</h1>
      <p>Follow the discussion below. The facilitator will select the ideas the group agrees to adopt and the page will update as the meeting moves forward.</p>
    </section>
    {visibleQuestions.map((q) => {
      const adopted = state.adopted?.[q.key] || [];
      return <section key={q.key} className="af-review-section">
        <div className="af-review-heading"><div><h2>{q.audience}: {q.title}</h2><p>{adopted.length ? "The group has adopted:" : "The group is still discussing this part of the strategy."}</p></div></div>
        {adopted.length > 0 && <div className="af-idea-grid">{adopted.map((idea, index) => <div className="af-idea-card selected" key={index}><p>{idea}</p><span>✓ ADOPTED</span></div>)}</div>}
      </section>;
    })}
  </Shell>;
}

export function AutoFundraiserMeetingPage() {
  const { token } = useParams();
  const [state, setState] = useState(null);
  const [adopted, setAdopted] = useState({});
  const [participationRoles, setParticipationRoles] = useState({});
  const [newIdeas, setNewIdeas] = useState({});
  const [notes, setNotes] = useState("");
  const [transcript, setTranscript] = useState("");
  const [confirmedRoles, setConfirmedRoles] = useState([]);
  const transcriptRef = useRef("");
  const saveQueue = useRef(Promise.resolve());
  const [saveMessage, setSaveMessage] = useState("");
  const [strategyDraft, setStrategyDraft] = useState(null);
  const [finalStrategy, setFinalStrategy] = useState(null);
  const [strategyShareToken, setStrategyShareToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const capture = useMeetingRecording({
    token, initialState: state, authHeaders,
    onTranscript: (text) => {
      transcriptRef.current = `${transcriptRef.current} ${text}`.trim();
      setTranscript(transcriptRef.current);
      saveTranscript(transcriptRef.current);
    },
    onError: setError,
  });

  useEffect(() => {
    document.title = "Group Fundraising Planning | Auto Fundraiser";
    axios.get(`${API}/meeting/${token}`, { headers: authHeaders() }).then(({ data }) => {
      setState(data);
      setAdopted(data.adopted || {});
      setNotes(data.meeting_notes || "");
      transcriptRef.current = data.meeting_transcript || "";
      setTranscript(transcriptRef.current);
      setConfirmedRoles(data.confirmed_roles || []);
      setStrategyDraft(data.strategy_draft || null);
      setFinalStrategy(data.final_strategy || null);
      setStrategyShareToken(data.strategy_share_token || "");

      const roles = { ...(data.adopted_participation || {}) };
      if (!roles.organizer && data.participation) roles.organizer = data.participation;
      (data.contributors || []).forEach((person) => {
        if (!roles[person.token] && person.participation) roles[person.token] = person.participation;
      });
      setParticipationRoles(roles);
    }).catch((err) => {
      if (err.response?.status === 401 || err.response?.status === 403) { window.location.href = `/login?resume_token=${encodeURIComponent(token)}`; return; }
      setError(errorMessage(err, "We could not open the group fundraising meeting."));
    });
  }, [token]);

  const sourceIdeasFor = (key) => {
    const ideas = [];
    const organizer = state?.strategy_answers?.[key];
    if (organizer) ideas.push({ source: state.organizer_name || "Organizer", text: organizer });
    (state?.contributors || []).forEach((person) => {
      const text = person.answers?.[key];
      if (text) ideas.push({ source: person.name, text });
    });
    return ideas;
  };

  const ideasFor = (key) => {
    const ideas = sourceIdeasFor(key);
    const sourceTexts = new Set(ideas.map((idea) => idea.text));
    (adopted[key] || []).forEach((text) => {
      if (!sourceTexts.has(text)) ideas.push({ source: "Created in this meeting", text, meetingIdea: true });
    });
    return ideas;
  };

  const hasBusiness = [6,7,8,9,10].some((n) => sourceIdeasFor(String(n)).length);
  const hasGrantors = [11,12,13,14,15].some((n) => sourceIdeasFor(String(n)).length);
  const visibleQuestions = STRATEGY_QUESTIONS.filter((q) => Number(q.key) <= 5 || (Number(q.key) <= 10 ? hasBusiness : hasGrantors));

  const participantRows = state ? [
    { key: "organizer", name: state.organizer_name || "Organizer", role: state.organizer_role || "", participation: state.participation || "" },
    ...(state.contributors || []).map((person) => ({ key: person.token, name: person.name, role: person.role || "", participation: person.participation || "" })),
  ] : [];

  const payload = (nextAdopted = adopted, nextNotes = notes, nextRoles = participationRoles, nextTranscript = transcriptRef.current, nextConfirmed = confirmedRoles) => ({
    adopted: nextAdopted,
    meeting_notes: nextNotes,
    participation_roles: nextRoles,
    meeting_transcript: nextTranscript,
    confirmed_roles: nextConfirmed,
  });

  const saveDraft = (nextAdopted = adopted, nextNotes = notes, nextRoles = participationRoles, nextTranscript = transcriptRef.current, nextConfirmed = confirmedRoles) => {
    const decisions = payload(nextAdopted, nextNotes, nextRoles, nextTranscript, nextConfirmed);
    setSaveMessage("");
    saveQueue.current = saveQueue.current.catch(() => {}).then(async () => {
      await axios.put(`${API}/meeting/${token}/draft`, decisions, { headers: authHeaders() });
      setSaveMessage("Saved");
    });
    saveQueue.current.catch((err) => setError(errorMessage(err, "We could not save the meeting decisions. Please retry before leaving this page.")));
    return saveQueue.current;
  };

  const saveTranscript = async (value = transcriptRef.current) => {
    try {
      await axios.put(`${API}/meeting/${token}/transcript`, { transcript: value }, { headers: authHeaders() });
    } catch (err) {
      setError(errorMessage(err, "We could not save the meeting notes. Please retry."));
    }
  };

  const stopRecording = async () => {
    setError("");
    try { await capture.stop(); await saveTranscript(); }
    catch (err) { setError(err.message || "We could not finish saving the recording."); }
  };

  const toggle = (key, text) => {
    const current = adopted[key] || [];
    const next = { ...adopted, [key]: current.includes(text) ? current.filter((item) => item !== text) : [...current, text] };
    setAdopted(next);
    saveDraft(next);
  };

  const addMeetingIdea = (key) => {
    const text = String(newIdeas[key] || "").trim();
    if (!text) return;
    const current = adopted[key] || [];
    const next = { ...adopted, [key]: current.includes(text) ? current : [...current, text] };
    setAdopted(next);
    setNewIdeas({ ...newIdeas, [key]: "" });
    saveDraft(next);
  };

  const updateParticipation = (key, value) => {
    setParticipationRoles((current) => ({ ...current, [key]: value }));
    setConfirmedRoles((current) => current.filter((item) => item !== key));
  };

  const canBuild = visibleQuestions.every((q) => (adopted[q.key] || []).length > 0)
    && participantRows.every((person) => String(participationRoles[person.key] || "").trim() && confirmedRoles.includes(person.key))
    && !capture.hasFailures && !capture.transcribing;
  const canBuildRecorded = canBuild && (capture.recording || capture.recordings.length > 0);

  const buildDraft = async () => {
    setBusy(true); setError("");
    try {
      await capture.stop();
      await saveQueue.current;
      await saveDraft();
      const { data } = await postGeneration(`${API}/meeting/${token}/adopt`, payload(), { headers: authHeaders() });
      if (data.professional_review_required) {
        const response = await axios.get(`${API}/meeting/${token}`, { headers: authHeaders() });
        setState(response.data);
        window.scrollTo({ top: 0 });
        return;
      }
      setStrategyDraft(data.strategy_draft);
      window.scrollTo({ top: 0 });
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "We could not build the strategy draft.");
    } finally {
      setBusy(false);
    }
  };

  const finalize = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/meeting/${token}/finalize`, {}, { headers: authHeaders() });
      setFinalStrategy(data.final_strategy);
      setStrategyShareToken(data.strategy_share_token || "");
      setStrategyDraft(null);
      setState((current) => ({ ...current, booking_url: data.booking_url, strategy_branding: data.strategy_branding }));
      window.scrollTo({ top: 0 });
    } catch (err) {
      setError(errorMessage(err, "We could not adopt the final strategy."));
    } finally {
      setBusy(false);
    }
  };

  if (!state && !error) return <Shell><Spinner label="Preparing your group fundraising meeting…" /></Shell>;
  if (error && !state) return <Shell narrow><div className="af-message-card"><h1>We couldn't open this planning meeting.</h1><p>{error}</p></div></Shell>;
  if (state?.professional_review_required && state?.professional_review_status === "pending" && !finalStrategy) return <Shell narrow><StrategySubmissionComplete organization={state.organization_name} /></Shell>;

  if (finalStrategy) {
    const downloadUrl = strategyShareToken ? `${API}/share/strategy/${strategyShareToken}/download` : "";
    return <Shell><section className="af-meeting-hero"><div className="af-kicker">YOUR FUNDRAISING STRATEGY IS READY</div><p>{deliveryMessage(finalStrategy)}</p></section><StrategyView strategy={finalStrategy} downloadUrl={downloadUrl} branding={state?.strategy_branding} bookingUrl={state?.booking_url} /><StrategyNextSteps token={token} headers={authHeaders()} />{state?.workflow?.board_portfolios && <BoardPortfolioManager token={token} />}</Shell>;
  }

  if (strategyDraft) return <Shell>
    <section className="af-meeting-hero">
      <div className="af-kicker">YOUR FUNDRAISING STRATEGY DRAFT IS READY</div>
      <h1>Read the draft together and adopt it when the group agrees this is your strategy.</h1>
      <p>Your team has contributed ideas and agreed on the decisions that shaped this draft. Read it together, consider how the recommendations support your mission and confirm that it is the direction your organization wants to carry forward.</p>
    </section>
    <StrategyView strategy={strategyDraft} draft />
    <section className="af-adopt-strategy">
      <h2>Does the group agree that this is the fundraising strategy you want to move forward with?</h2>
      <button className="af-primary af-primary-wide" disabled={busy} onClick={finalize}>{busy ? "ADOPTING…" : "YES, ADOPT THIS AS OUR FUNDRAISING STRATEGY"} <span>→</span></button>
      <button className="af-secondary af-primary-wide" disabled={busy} onClick={() => { setStrategyDraft(null); saveDraft(); }}>GO BACK AND CHANGE OUR DECISIONS</button>
      {error && <p className="af-error">{error}</p>}
    </section>
  </Shell>;

  const participantUrl = state?.meeting_share_token ? `${window.location.origin}/game/${state.meeting_share_token}` : "";

  return <Shell>
    <section className="af-meeting-hero">
      <div className="af-kicker">GROUP FUNDRAISING GAME</div>
      <h1>Agree on the fundraising strategy together.</h1>
      {!state?.transcription_available && <p>We save your audio and use your browser's speech recognition for the discussion. This does not call our paid transcription service. Check the discussion text before building the strategy, and correct or add anything the browser missed.</p>}
      <p>Start with individual donors, then businesses and grantors where they belong. Review everyone's ideas, select what the group agrees with, create stronger answers where needed and agree on how each person will participate.</p>
      <div className="af-meeting-start-actions">
        {!capture.recording ? <button className="af-primary" disabled={capture.transcribing || busy} onClick={capture.start}>START GAME & OPEN MICROPHONE</button> : <button className="af-secondary" disabled={capture.transcribing || busy} onClick={stopRecording}>STOP MICROPHONE & SAVE TRANSCRIPT</button>}
        {capture.recording && <span className="af-recording-live">● MICROPHONE LIVE</span>}
        {capture.transcribing && <span>Saving the recording and its transcript. Please keep this page open.</span>}
      </div>
      {participantUrl && <div className="af-share-game-inline"><strong>Participant view:</strong><input aria-label="Meeting participant link" readOnly value={participantUrl} /><CopyLinkButton label="COPY" value={participantUrl} /></div>}
    </section>

    {error && <p className="af-error">{error}</p>}

    <section className="af-work-card af-transcript-card">
      <div className="af-kicker">MEETING TRANSCRIPT</div>
      <h2>The discussion matters too.</h2>
      <p>The final strategy will use the group's selected ideas first and the transcript to understand the reasoning, agreements and context behind those decisions.</p>
      <p>Open the microphone before the discussion. We save the audio in short parts and turn it into a transcript. When you build the draft, we finish saving the recording first so the whole discussion can be included.</p>
      {capture.recordedTranscript && <div className="af-recorded-transcript"><strong>Saved recording transcript</strong><p>{capture.recordedTranscript}</p></div>}
      <label className="af-full-label">Extra notes or corrections to the discussion
        <textarea rows={7} value={transcript} onChange={(e) => { transcriptRef.current = e.target.value; setTranscript(e.target.value); }} onBlur={() => saveTranscript()} placeholder="Add an important decision or correct something in the recorded discussion here." />
      </label>
      {!!capture.recordings.length && <div className="af-audio-review"><strong>Saved meeting audio</strong>{capture.recordings.map((piece, index) => <div key={piece.audio_token}>
        <span>Part {index + 1}: {piece.status === "ready" ? "Audio and transcript saved" : (piece.status === "browser" || !state?.transcription_available) ? "Audio saved" : "Transcript needs another attempt"}</span>
        <button className="af-secondary" onClick={() => capture.download(piece)}>DOWNLOAD AUDIO PART {index + 1}</button>
      </div>)}</div>}
      {capture.hasFailures && <button className="af-secondary" disabled={capture.transcribing} onClick={capture.retry}>RETRY SAVING & TRANSCRIBING THE RECORDING</button>}
    </section>

    {visibleQuestions.map((q, index) => <section key={q.key} className="af-review-section">
      <div className="af-review-heading"><span>{String(index + 1).padStart(2, "0")}</span><div><div className="af-card-label">{q.audience.toUpperCase()} • {q.step}</div><h2>{q.title}</h2><p>{q.hint}</p></div></div>
      <div className="af-idea-grid">
        {ideasFor(q.key).map((idea, i) => {
          const selected = (adopted[q.key] || []).includes(idea.text);
          return <button key={i} className={`af-idea-card ${selected ? "selected" : ""}`} onClick={() => toggle(q.key, idea.text)}>
            <div className="af-idea-source">{idea.source}</div><p>{idea.text}</p><span>{selected ? "✓ ADOPTED" : "SELECT THIS IDEA"}</span>
          </button>;
        })}
      </div>
      <div className="af-new-idea-box">
        <div>
          <strong>Did the discussion produce a better answer?</strong>
          <p>Write the answer the group agrees with. Saving it adopts it for this part of the strategy.</p>
        </div>
        <textarea rows={3} value={newIdeas[q.key] || ""} onChange={(e) => setNewIdeas({ ...newIdeas, [q.key]: e.target.value })} placeholder="Write the new agreed answer…" />
        <button className="af-secondary" disabled={!String(newIdeas[q.key] || "").trim()} onClick={() => addMeetingIdea(q.key)}>SAVE & ADOPT THIS ANSWER</button>
      </div>
    </section>)}

    <section className="af-participation-review">
      <div className="af-kicker">HOW EACH PERSON WILL PARTICIPATE</div>
      <h2>Agree on each person's actual fundraising role.</h2>
      <p>Discuss the roles people are comfortable playing and agree on each person's responsibility. These commitments will be included in the final fundraising strategy.</p>
      <div className="af-participation-role-grid">
        {participantRows.map((person) => <article key={person.key} className="af-work-card">
          <strong>{person.name}{person.role ? ` · ${person.role}` : ""}</strong>
          <p><b>What they originally offered:</b> {person.participation}</p>
          <label className="af-full-label">Agreed fundraising role
            <textarea rows={4} value={participationRoles[person.key] || ""} onChange={(e) => updateParticipation(person.key, e.target.value)} onBlur={() => saveDraft(adopted, notes, participationRoles, transcript)} />
          </label>
          <button className="af-secondary" disabled={!String(participationRoles[person.key] || "").trim() || confirmedRoles.includes(person.key)} onClick={() => {
            const next = [...confirmedRoles, person.key];
            setConfirmedRoles(next);
            saveDraft(adopted, notes, participationRoles, transcriptRef.current, next);
          }}>{confirmedRoles.includes(person.key) ? "ROLE AGREED" : "AGREE ON THIS ROLE"}</button>
        </article>)}
      </div>
    </section>

    <section className="af-work-card">
      <h2>Anything else the group decided?</h2>
      <p>Capture any decision or context that should shape the strategy but does not fit neatly into one of the answers above.</p>
      <textarea rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={() => saveDraft(adopted, notes, participationRoles, transcript)} placeholder="Additional group decisions or context…" />
      {saveMessage && <p className="af-save-message">{saveMessage}</p>}
    </section>

    <div className="af-build-final">
      <button className="af-primary af-primary-wide" disabled={busy || !canBuildRecorded} onClick={buildDraft}>{busy ? "BUILDING THE STRATEGY…" : state?.workflow?.review_group ? "SUBMIT OUR STRATEGY FOR PROFESSIONAL REVIEW" : "BUILD OUR FUNDRAISING STRATEGY DRAFT"} <span>→</span></button>
      {!canBuildRecorded && <p>Record the discussion, adopt an answer for every audience section shown, and agree on each person's fundraising role before building the draft.</p>}
    </div>
  </Shell>;
}
